// Copyright (C) AirWorks Solutions, Inc - All Rights Reserved
// DO NOT REDISTRIBUTE
// UNAUTHORIZED COPYING OF THIS FILE, ANY PART OR WHOLE, VIA ANY MEDIUM IS STRICTLY PROHIBITED
// PROPRIETARY AND CONFIDENTIAL

import React, { useEffect, useRef, useState, FC, useCallback, useMemo } from 'react';
import { useAppSelector, useAppDispatch } from 'Hooks';
import { useSelector } from 'react-redux';
import { RootState } from 'Store';
import SVG from 'react-inlinesvg';
import Map from '@arcgis/core/Map';
import MapView from '@arcgis/core/views/MapView';
import SceneView from '@arcgis/core/views/SceneView';
import GroupLayer from '@arcgis/core/layers/GroupLayer';
import Zoom from '@arcgis/core/widgets/Zoom.js';
import NavigationToggle from '@arcgis/core/widgets/NavigationToggle.js';
import Compass from '@arcgis/core/widgets/Compass.js';
import Home from '@arcgis/core/widgets/Home.js';
import Viewpoint from '@arcgis/core/Viewpoint.js';
import Measurement from '@arcgis/core/widgets/Measurement.js';
import DistanceMeasurement2D from '@arcgis/core/widgets/DistanceMeasurement2D';
import AreaMeasurement2D from '@arcgis/core/widgets/AreaMeasurement2D';
import FeatureFilter from '@arcgis/core/layers/support/FeatureFilter';
import DirectLineMeasurement3D from '@arcgis/core/widgets/DirectLineMeasurement3D';
import AreaMeasurement3D from '@arcgis/core/widgets/AreaMeasurement3D';
import PointCloudLayer from '@arcgis/core/layers/PointCloudLayer';
import GeoJSONLayer from '@arcgis/core/layers/GeoJSONLayer.js';
import MapImageLayer from '@arcgis/core/layers/MapImageLayer';
import FeatureLayer from '@arcgis/core/layers/FeatureLayer';
import * as query from '@arcgis/core/rest/query';
import Query from '@arcgis/core/rest/support/Query';
import LabelClass from '@arcgis/core/layers/support/LabelClass';
import esriRequest from '@arcgis/core/request';
import Color from '@arcgis/core/Color';
import BaseTileLayer from '@arcgis/core/layers/BaseTileLayer';
import GraphicsLayer from '@arcgis/core/layers/GraphicsLayer';
import * as identify from '@arcgis/core/rest/identify';
import IdentifyParameters from '@arcgis/core/rest/support/IdentifyParameters';
import * as reactiveUtils from '@arcgis/core/core/reactiveUtils';
import Graphic from '@arcgis/core/Graphic';
import SpatialReference from '@arcgis/core/geometry/SpatialReference';
import PopupTemplate from '@arcgis/core/PopupTemplate';
import BasemapGallery from '@arcgis/core/widgets/BasemapGallery.js';
import * as projectOperator from '@arcgis/core/geometry/operators/projectOperator.js';
import * as distanceOperator from '@arcgis/core/geometry/operators/distanceOperator.js';
import '@arcgis/core/assets/esri/themes/light/main.css';
import '@esri/calcite-components/dist/components/calcite-switch';
import '@esri/calcite-components/dist/calcite/calcite.css';
import esriId from '@arcgis/core/identity/IdentityManager.js';
import Point from '@arcgis/core/geometry/Point';
import { GetProjectDetailsRoute } from 'Utils/routes';
import ReactPannellum, { getConfig } from 'react-pannellum';
import type { ElevationInfo } from '@arcgis/core/layers/support/ElevationInfo';
import Extent from '@arcgis/core/geometry/Extent';
import { Box } from '@mui/system';
import { useNavigate, useParams } from 'react-router-dom';
import LoadingSpinner from 'Components/LoadingSpinner';
import Slider from '@arcgis/core/widgets/Slider';

// Redux selectors
import eyeVisible from 'Assets/view-visible.svg';
import eyeHidden from 'Assets/view-hidden.svg';
import { classification6Classes, classification6ClassesNew, orderStatusCode } from 'Utils/constants';
import { IconButton, SvgIcon, Typography, ClickAwayListener, CircularProgress } from '@mui/material';
import { getOrders } from 'Features/order/orderSelectors';
import { SetOrderSelectedAction } from 'Features/map/mapEditorActions';
import { GetProjectThunk } from 'Features/project/projectThunk';
import { GetRasterTilesThunk, GetVectorTilesThunk } from 'Features/map/mapCommonThunk';
import { GetOrdersThunk } from 'Features/order/orderThunk';
import { forEach } from 'jszip';
import { ResetKmlAction } from 'Features/kml/kmlActions';
import { GetEsriGeoJsonUrl } from 'Config';
import getRendererForConfig from 'Utils/esriRenderer';
import { LoadKmlsThunk } from '../kml/kmlThunk';
import AreaMeasurement from './AreaCustomWidget';
import DistanceMeasurement from './DistanceCustomWidget';
import Annotation from './Annotation';
import LabelEditor from './Label/LabelEditor';
import TerrainWidget from './TerrainWidget';
import Basemap from './Basemap';
import AOI from './AOIWidget';
import { getFeatureCollection } from '../kml/kmlSelectors';
import FilesSidebar from './Sidebar/index';
import { SetProjectIdAction } from '../project/projectActions';
import stylesDef from '../files/styles';
import getStyles from './styles';
import config from './mainconfig';
import { GetAllAnnotationThunk, LoadPanoFeaturesThunk, getSignedURLThunk } from './esriSplitViewerThunk';
import { ClearAnnotationSuccessAction, ClearPanoFeaturesAction } from './esriSplitViewerActions';
import PopupWindow from './PopupWindow';
import { styleConfig } from './styleConfig';

const mapImageLayerIndex = [0, 1, 2, 3]; // Sub-layer indexes (text, point, polyline, polygon)of mapimage Layer for optimized queries

const RasterTileLayer = BaseTileLayer.createSubclass({
  properties: {
    urlTemplate: null,
    isTms: false,
    tint: {
      value: null,
      type: Color,
    },
  },
  initialize() {
    reactiveUtils.watch(
      () => this.tint,
      () => {
        this.refresh();
      },
    );
  },
  getTileUrl(level, row, col) {
    let y = row; // default XYZ
    if (this.isTms) {
      y = 2 ** level - row - 1; // flip for TMS
    }
    return this.urlTemplate
      .replace('{z}', level)
      .replace('{x}', col)
      .replace('{y}', y);
  },
  fetchTile(level, row, col, options) {
    const url = this.getTileUrl(level, row, col);
    return esriRequest(url, {
      responseType: 'image',
      signal: options?.signal,
    }).then(
      (response) => {
        const image = response.data;
        const width = this.tileInfo.size[0];
        const height = this.tileInfo.size[1];

        const canvas = document.createElement('canvas');
        const context = canvas.getContext('2d');
        canvas.width = width;
        canvas.height = height;

        if (this.tint) {
          context.fillStyle = this.tint.toCss();
          context.fillRect(0, 0, width, height);
          context.globalCompositeOperation = 'difference';
        }

        context.drawImage(image, 0, 0, width, height);
        return canvas;
      },
    );
  },
});
//  styling configuration
interface UniqueValue {
  value: string;
  color: string;
  linetype: string;
  linewidth: number;
  label: string;
  font_size: number;
}

const EsriSplitViewer: FC = () => {
  const params = useParams();

  const lasRef = useRef<HTMLDivElement | null>(null);
  const panoRef = useRef<HTMLDivElement | null>(null);
  const coneGraphicRef = useRef(null);
  const centerPointRef = useRef<__esri.Point | null>(null);
  const featureKappaRef = useRef<number>(0);
  const [lasVisible, setLasVisible] = useState(true);
  const [panoVisible, setPanoVisible] = useState(false);
  const [panoControlsEnabled, setPanoControlsEnabled] = useState(false);
  const lasViewRef = useRef<__esri.SceneView | null>(null);
  const geoLayerRef = useRef<__esri.GeoJSONLayer | null>(null);
  const panoLayerRef = useRef<__esri.GeoJSONLayer | null>(null);
  const mapInstanceRef = useRef<__esri.Map | null>(null);
  const lasMapInstanceRef = useRef<__esri.Map | null>(null);
  const [featureList, setFeatureList] = useState<UniqueValue[]>([]);
  const featureListRef = useRef<UniqueValue[]>([]);
  const [checkedItems, setCheckedItems] = useState<{ [key: string]: boolean }>({});
  const [rasterLayers, setRasterLayers] = useState<any[]>([]);
  const [pointCloudLayers, setPointCloudLayers] = useState<PointCloudLayer[]>([]);
  const [mapImageLayer, setMapImageLayer] = useState<MapImageLayer | null>(null);
  const [featureLayers, setFeatureLayers] = useState<FeatureLayer[]>([]);
  const [activeCadId, setActiveCadId] = useState<string | null>(null);
  const [warnings, setWarnings] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [tokenRegistered, setTokenRegistered] = useState(false);
  const [showPanoLayer, setShowPanoLayer] = useState(true);
  const views: (SceneView | MapView)[] = [];
  const [showPointCloud, setShowPointCloud] = useState(true);
  const [showClassifiedPointCloud, setShowClassifiedPointCloud] = useState({});
  const [resetZoom, setResetZoom] = useState(false);
  const [isSidebarVisible, setIsSidebarVisible] = useState(true);
  const viewportCenter = useSelector((state: RootState) => state.map.common.viewport);

  const [popupVisible, SetPopupVisibility] = useState(false);
  const [distanceWidget3d, setDistanceWidget3d] = useState(null);
  const [AreaWidget3d, setAreaWidget3d] = useState(null);
  const [classVisibility, setClassVisibility] = useState<{ [fileTitle: string]: { [classId: string]: boolean } }>({});
  const pointCloudLayersRef = useRef<PointCloudLayer[]>([]);
  const panoLayerFeatureRef = useRef<__esri.FeatureLayer | null>(null);
  const point3DLayerRef = useRef<__esri.FeatureLayer | null>(null);
  const currentLayersRef = useRef(null);
  const terrainRef = useRef(null);
  const [isHovered, setIsHovered] = useState(false);
  const [panoError, setPanoError] = useState<string | null>(null);
  const [panoWarning, setPanoWarning] = useState<string | null>(null);
  const [panoErrorClosed, setPanoErrorClosed] = useState(false);
  const panoNextFeatureRef = useRef<__esri.Graphic | null>(null);
  const panoPrevFeatureRef = useRef<__esri.Graphic | null>(null);
  // ADJUST PANEL STATE (inside viewer)
  const [adjustPanelOpen, setAdjustPanelOpen] = useState<'classified' | 'output' | 'input' | null>(null);
  const [classifiedPanelOpen, setClassifiedPanelOpen] = useState(false);
  const [outputPanelOpen, setOutputPanelOpen] = useState(false);
  const [inputPanelOpen, setInputPanelOpen] = useState(false);
  const inputSliderRef = useRef<Slider | null>(null);
  const outputSliderRef = useRef<Slider | null>(null);
  const classSliderRef = useRef<Slider | null>(null);
  const [geodatabaseLayers, setGeodatabaseLayers] = useState<any[]>([]);
  const [shapefileLayers, setShapefileLayers] = useState<any[]>([]);
  const geodatabaseLayersRef = useRef<any[]>([]);
  const shapefileLayersRef = useRef<any[]>([]);

  // Get feature collection from Redux
  const featureCollection = useSelector(getFeatureCollection);
  const [geoLayer, setGeoLayer] = useState<GeoJSONLayer | null>(null);
  const rasterTileJson = useSelector((state: RootState) => state.map.common.rasterTileJson);
  const projectData = useAppSelector((state) => state.project.project);
  const orders = useSelector((state: RootState) => state.order.list);
  const visibleCount = [lasVisible, panoVisible].filter(Boolean).length;
  const visibleCountStyle = Object.values(visibleCount).filter((v) => v).length;
  const viewWidth = visibleCount > 0 ? `${100 / visibleCount}%` : '100%';
  const styles = getStyles(viewWidth, { las: lasVisible, pano: panoVisible }, visibleCount, lasVisible, panoVisible);
  const panoFeatures = useSelector((state: RootState) => state.esri.pano.features);
  const AnnotationFeatures = useAppSelector((state) => state.esri.annotation.list);
  const [imageSource, setImageSource] = useState<string | null>(null);
  const currentFeatureRef = useRef<__esri.Graphic | null>(null);
  const currentSceneIdRef = useRef<string>('firstScene');
  const [failedPanoIds, setFailedPanoIds] = useState<string[]>([]);
  const style = stylesDef();
  const dispatch = useAppDispatch();
  const { id } = useParams();
  const [popupData, setPopupData] = useState(null);
  const [popupIndex, setPopupIndex] = useState(0);
  const [filteredFeatures, setFilteredFeatures] = useState<any[]>([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [showPopup, setShowPopup] = useState(false);
  const [MeasuringDistance, SetMeasurmentActiveDis] = useState(false);
  const [MeasuringArea, SetMeasurmentActiveArea] = useState(false);
  const measuringRefDistance = useRef(MeasuringDistance);
  const measuringRefArea = useRef(MeasuringArea);
  const [popupXY, setPopupXY] = useState<{ x: number; y: number } | null>(null);
  const [lasViewref, setlasView] = useState(null);
  const islasViewRef = useRef(false);
  const [isAdding, setIsAdding] = useState(false);
  const isAddingRef = useRef(isAdding);
  const isAddingRef3d = useRef(isAdding);
  const ifStart = useRef(false);
  const [AnnotationEdit, setAnnotationEdit] = useState(null);
  const annotationWidgetRef = useRef(null);
  const annotationWidgetRef3d = useRef(null);
  const [annotations, setAnnotations] = useState([]);
  const [annotations3d, setAnnotations3d] = useState([]);
  const existingAnnotaion = useRef([]);
  const newLabelRef = useRef(false);
  const basemapRef = useRef(null);
  const [homeWidget, setHomeWidget] = useState<any>(null);
  const [aoiWidget, setAoiWidget] = useState<any>(null);
  const [overlaySize, setOverlaySize] = useState({ width: 0, height: 0 });
  const [overlaySize3d, setOverlaySize3d] = useState({ width: 0, height: 0 });
  const [overlayposition, setOverlayPosition] = useState({ top: 0, left: 0 });
  const [overlayposition3d, setOverlayPosition3d] = useState({ top: 0, left: 0 });
  const [orderid, setorderid] = useState(null);
  const orderref = useRef(null);
  const initailExtentref = useRef(null);
  const lastKmlStringRef = useRef<string>('');
  const lastPanoStringRef = useRef<string>('');
  const lastRasterStringRef = useRef<string>('');
  const lastPointCloudStringRef = useRef<string>('');
  const lastMapUsedRef = useRef<__esri.Map | null>(null);
  const lastOrderIdRef = useRef<string | null>(null);
  const lastPointCloudMapRef = useRef<__esri.Map | null>(null);
  const otherwidgetactiveref = useRef(false);
  // Flatten all cadFiles from orders
  const cadFiles = orders?.flatMap((order: any) => order.cadFiles || []) || [];
  const [projectId, setProjectId] = useState<string | null>(null);
  const navigate = useNavigate();
  const selectedOrderId = useAppSelector((state) => state.map.editor.present.orderSelected);
  const placedOrders = useAppSelector((state) => getOrders(state).placedOrders);
  const sortedPlacedOrders = useMemo(
    () =>
      [...placedOrders].sort((a, b) => {
        const dateA = new Date(a.createdAt || 0).getTime();
        const dateB = new Date(b.createdAt || 0).getTime();
        return dateB - dateA;
      }),
    [placedOrders],
  );
  const orderIdList = sortedPlacedOrders.map((order) => order._id);
  const selectedOrder = sortedPlacedOrders.find((o) => o._id === selectedOrderId);
  const cadIdsForOrder = (selectedOrder?.cadFiles ?? []).map((c: any) => c._id);
  const kmlFeaturesForOrder = (selectedOrder?.cadFiles ?? []).flatMap((c: any) => c.kmlFeatures ?? []);
  const [adjustPanelPosition, setAdjustPanelPosition] = useState<{ top: number; left: number } | null>(null);
  const [classifiedPointSize, setClassifiedPointSize] = useState<number>(2);
  const [outputPointSize, setOutputPointSize] = useState<number>(10);
  const [inputPointSize, setInputPointSize] = useState<number>(2);

  const getLasTitle = useCallback((las: any): string => {
    const fullPath = las.filename || '';
    const index = projectData?.lasFiles ? projectData.lasFiles.indexOf(las) : 0;
    const filename =
      fullPath.substring(fullPath.lastIndexOf('/') + 1) ||
      las.lasFilename ||
      `PointCloud-${index >= 0 ? index : 0}`;
    return filename.replace(/\.(las|laz)$/, '');
  }, [projectData?.lasFiles]);

  const openAdjustPanel = useCallback((panelType: 'classified' | 'output' | 'input') => {
    // Reset position when switching panels
    setAdjustPanelPosition(null);

    switch (panelType) {
    case 'classified':
      setClassifiedPanelOpen((prev) => !prev);
      break;
    case 'output':
      setOutputPanelOpen((prev) => !prev);
      break;
    case 'input':
      setInputPanelOpen((prev) => !prev);
      break;
    }
  }, []);

  const getAdjustPanelStyles = (baseStyles: any) => ({
    ...baseStyles,
    position: 'fixed' as const,
    top: adjustPanelPosition ? `${adjustPanelPosition.top + 8}px` : '50%',
    left: adjustPanelPosition ? `${adjustPanelPosition.left}px` : '50%',
    transform: adjustPanelPosition ? 'none' : 'translate(-50%, -50%)',
    zIndex: 10000,
  });

  const initialLasStatuses = useMemo(() => {
    if (!projectData?.lasFiles || !Array.isArray(projectData?.lasFiles)) {
      return {};
    }
    return projectData?.lasFiles.reduce((acc, file) => {
      const title = getLasTitle(file);
      acc[title] = file.lasType === 'unclassified'; // Default visible
      return acc;
    }, {} as { [key: string]: boolean });
  }, [projectData?.lasFiles, getLasTitle]);

  const [lasFilesDisplayStatuses, setLasFilesDisplayStatuses] = useState<{ [key: string]: boolean }>(initialLasStatuses);

  useEffect(() => { featureListRef.current = featureList; }, [featureList]);

  useEffect(() => {
    // Sync lasFilesDisplayStatuses with initialLasStatuses to pick up new files
    // while preserving existing user choices.
    setLasFilesDisplayStatuses((prev) => ({
      ...initialLasStatuses,
      ...prev,
    }));
  }, [initialLasStatuses]);

  const createPointCloudLayers = useCallback(() => {
    try {
      if (!projectData?.lasFiles || !Array.isArray(projectData?.lasFiles)) {
        console.warn('No lasFiles found in project data.');
        setShowPointCloud(false);
        return [];
      }

      const validLasFiles = projectData?.lasFiles.filter((file: any) => {
        const hasUrl = !!file.esriUrl;
        const hasNoError = !file.esriError || file.esriError.toLowerCase() === 'success';

        if (!hasUrl) {
          console.warn(`Skipping LAS file ${file.filename}: No esriUrl`);
        }
        if (!hasNoError) {
          console.warn(`Skipping LAS file ${file.filename}: Error - ${file.esriError}`);
        }

        return hasUrl && hasNoError;
      });

      if (!validLasFiles.length) {
        console.warn('No valid LAS files found.');
        setShowPointCloud(false);
        return [];
      }

      const latestOutputByOrder: { [orderId: string]: any } = {};
      projectData?.lasFiles
        .filter((f: any) => f.ownerOrder)
        .forEach((f: any) => {
          const orderId = f.ownerOrder;
          if (!latestOutputByOrder[orderId]) {
            latestOutputByOrder[orderId] = f;
          } else {
            const prev = latestOutputByOrder[orderId];
            const prevTime = new Date(prev.createdAt || 0).getTime();
            const curTime = new Date(f.createdAt || 0).getTime();
            if (curTime >= prevTime) latestOutputByOrder[orderId] = f;
          }
        });

      const classSet = Array.from({ length: 65 }, (_, i) => i + 1);
      const newClassVisibility: { [fileTitle: string]: { [classId: string]: boolean } } = {};

      const layers = validLasFiles.map((file: any, index) => {
        const { lasType, newClasses, filename, lasFilename, ownerOrder, esriOffsetZ } = file;
        let renderer: any = null;

        if (lasType === 'all_classes') {
          if (!ownerOrder) return null;
          const latestForOrder = latestOutputByOrder[ownerOrder];
          if (!latestForOrder || latestForOrder._id !== file._id) return null;
        }

        const classList = lasType === 'all_classes' && newClasses === true
          ? classification6ClassesNew
          : classification6Classes;

        const knownClassIds = classList.map((c: any) => c.classId);
        const colorUniqueValueInfos = classList.map((c: any) => ({
          values: [String(c.classId)],
          label: c.name,
          color: c.color,
          originalColor: c.color,
        }));

        classSet.forEach((id) => {
          if (!knownClassIds.includes(id)) {
            colorUniqueValueInfos.push({
              values: [String(id)],
              label: `Undefined Class ${id}`,
              color: [255, 255, 255, 255],
              originalColor: [255, 255, 255, 255],
            });
          }
        });

        if (lasType === 'all_classes') {
          renderer = {
            type: 'point-cloud-unique-value',
            field: 'CLASS_CODE',
            colorUniqueValueInfos,
            pointSizeAlgorithm: { type: 'fixed-size', size: 1 },
          };
        } else if (lasType === 'unclassified') {
          renderer = {
            type: 'point-cloud-rgb',
            field: 'RGB',
            pointSizeAlgorithm: { type: 'fixed-size', size: 1 },
          };
        }

        const title = getLasTitle(file);

        newClassVisibility[title] = classList.reduce((acc, c) => {
          acc[String(c.classId)] = true;
          return acc;
        }, {} as { [k: string]: boolean });

        const visibleInitial = lasFilesDisplayStatuses[title] !== undefined
          ? lasFilesDisplayStatuses[title]
          : lasType === 'unclassified'
            ? true
            : ownerOrder === selectedOrderId;

        const offset = esriOffsetZ ?? 0;

        const layer = new PointCloudLayer({
          url: `${file.esriUrl}`,
          title,
          ...(renderer && { renderer }),
          visible: visibleInitial,
          elevationInfo: {
            mode: 'absolute-height',
            ...(offset !== 0 && { offset }),
          },
        });
        (layer as any).ownerOrder = ownerOrder;
        (layer as any).lasType = lasType;

        return layer;
      });

      setClassVisibility((prev) => ({ ...prev, ...newClassVisibility }));
      const createdLayers = (layers.filter(Boolean) as PointCloudLayer[]) || [];
      setShowPointCloud(createdLayers.length > 0);
      return createdLayers;
    } catch (err) {
      console.error('Error creating PointCloudLayers:', err);
      return [];
    }
  }, [projectData?.lasFiles, lasFilesDisplayStatuses]);

  useEffect(() => {
    if (!params.id) {
      navigate(GetProjectDetailsRoute(params.id));
    } else {
      const onLoad = async () => {
        const projectId = params.id;
        if (projectId) {
          dispatch(SetProjectIdAction(projectId));
          await dispatch(GetProjectThunk());
          await dispatch(GetOrdersThunk(projectId));
          await dispatch(GetAllAnnotationThunk());
          await dispatch(GetRasterTilesThunk());
          await dispatch(GetVectorTilesThunk());
          await dispatch(LoadKmlsThunk());
          await dispatch(LoadPanoFeaturesThunk());
          setLoading(false);
        }
      };

      // Register token BEFORE loading layers to prevent credential dialog
      const registerTokenAndLoad = async () => {
        try {
          const arcgisBaseUrl = `https://enterprise.arcgis-${_ENV_}.airworks.io`;
          const response = await fetch(`${arcgisBaseUrl}/DotNet_Test/proxy.ashx?getToken_AW`);
          const tokenResponse = await response.json();
          const { token } = tokenResponse;
          const expires = Number(tokenResponse.expires);
          esriId.registerToken({
            server: `${arcgisBaseUrl}/server/rest/services`,
            token,
            expires,
          });
          setTokenRegistered(true);
        } catch (err) {
          console.error('Token fetch failed:', err);
        }
        onLoad();
      };

      registerTokenAndLoad();
    }
    return () => {
      dispatch(SetProjectIdAction(null));
      ReactPannellum.destroy();
      dispatch(ClearPanoFeaturesAction());
      dispatch(ClearAnnotationSuccessAction());
      dispatch(ResetKmlAction());
    };
  }, []);
  // Define toggleTerrain at component level
  const toggleTerrain = async (maps: MapItem[], visible: boolean) => {
    const updateGroundVisibility = async (map: __esri.Map | null, mapName: string) => {
      if (!map) {
        console.warn(`${mapName} instance not available for toggleTerrain`);
        return;
      }

      try {
        await map.ground.when();
        map.ground.opacity = visible ? 1 : 0;

        if (mapName === 'CAD Map' && map.basemap) {
          map.basemap.baseLayers.forEach((layer) => {
            layer.visible = visible;
          });
        }
      } catch (err) {
        console.error(`${mapName} ground load error:`, err);
      }
    };
    for (const { map, name } of maps) {
      await updateGroundVisibility(map, name);
    }
  };
  (window as any).toggleTerrain = toggleTerrain;

  // Define toggleLasVisibility function
  const toggleLasVisibility = (filename: string, visible: boolean) => {
    setLasFilesDisplayStatuses((prev) => ({ ...prev, [filename]: visible }));
    const layer = pointCloudLayers.find((pcl) => pcl.title === filename);
    if (layer) {
      layer.visible = visible;
    }
  };

  const toggleUnknownClassVisibility = async (fileTitle: string, visible: boolean, knownVisibleOverride?: number[]) => {
    // Update the visibility state for the "Unknown" class
    setClassVisibility((prev) => {
      const updated = {
        ...prev,
        [fileTitle]: {
          ...(prev[fileTitle] || {}),
          '-1': visible, // Use '-1' as the key for Unknown class
        },
      };
      return updated;
    });

    const layer = pointCloudLayersRef.current.find((l) => l.title === fileTitle);
    if (!layer) return;
    if (!layer.loaded) await layer.load();

    // Get all visible known classes
    const knownVisible = knownVisibleOverride !== undefined
      ? knownVisibleOverride
      : Object.entries(classVisibility[fileTitle] || {})
        .filter(([id, v]) => id !== '-1' && v) // Exclude Unknown class ID
        .map(([id]) => parseInt(id));

    // Get the classification list for this file
    const lasFile = projectData?.lasFiles?.find((f: any) => getLasTitle(f) === fileTitle);
    const classList = lasFile?.lasType === 'all_classes' && lasFile?.newClasses
      ? classification6ClassesNew
      : classification6Classes;
    const knownClassIds = classList.map((c: any) => c.classId);

    // Get all unknown class IDs (1-65 excluding known ones)
    const unknownClassIds = Array.from({ length: 65 }, (_, i) => i + 1)
      .filter((id) => !knownClassIds.includes(id));

    // Build the final list of visible classes
    let combinedVisibleClasses: number[] = [];

    if (knownVisible.length === 0 && !visible) {
      // If no known classes are visible AND unknown is off, hide everything
      layer.visible = false;
      layer.filters = [];
      layer.definitionExpression = '1=0';
      setLasFilesDisplayStatuses((p) => ({ ...p, [fileTitle]: false }));
      return;
    }

    // Combine known visible classes with unknown classes (if visible)
    combinedVisibleClasses = [...knownVisible];
    if (visible) {
      combinedVisibleClasses = [...combinedVisibleClasses, ...unknownClassIds];
    }

    // If there are visible classes, show the layer
    if (combinedVisibleClasses.length > 0) {
      layer.visible = true;
      setLasFilesDisplayStatuses((p) => ({ ...p, [fileTitle]: true }));

      layer.filters = [{
        type: 'value',
        field: 'CLASS_CODE',
        mode: 'include',
        values: combinedVisibleClasses,
      }];
      layer.definitionExpression = `CLASS_CODE IN (${combinedVisibleClasses.join(',')})`;
    } else {
      layer.visible = false;
      layer.filters = [];
      layer.definitionExpression = '1=0';
      setLasFilesDisplayStatuses((p) => ({ ...p, [fileTitle]: false }));
    }
  };

  const toggleClassVisibility = async (fileTitle: string, classId: string, visible: boolean) => {
    let newVisibleClasses: number[] = [];

    setClassVisibility((prev) => {
      const updated = {
        ...prev,
        [fileTitle]: { ...(prev[fileTitle] || {}), [classId]: visible },
      };

      newVisibleClasses = Object.entries(updated[fileTitle])
        .filter(([_, v]) => v)
        .map(([id]) => parseInt(id));

      return updated;
    });

    const layer = pointCloudLayersRef.current.find((l) => l.title === fileTitle);
    if (!layer) return;
    if (!layer.loaded) await layer.load();

    // Check if Unknown class is visible
    const unknownVisible = classVisibility[fileTitle]?.['-1'] !== false;

    if (newVisibleClasses.length === 0) {
      layer.visible = false;
      layer.filters = [];
      layer.definitionExpression = '1=0';
      setLasFilesDisplayStatuses((p) => ({ ...p, [fileTitle]: false }));
      return;
    }

    layer.visible = true;
    setLasFilesDisplayStatuses((p) => ({ ...p, [fileTitle]: true }));

    // Get known class IDs for this las file
    const lasFile = projectData?.lasFiles?.find((f: any) => getLasTitle(f) === fileTitle);
    const classList = lasFile?.lasType === 'all_classes' && lasFile?.newClasses
      ? classification6ClassesNew
      : classification6Classes;
    const knownClassIds = classList.map((c: any) => c.classId);

    const unknownClassIds = Array.from({ length: 65 }, (_, i) => i + 1)
      .filter((id) => !knownClassIds.includes(id));

    // Combine visible known classes + all unknown classes
    const combinedVisibleClasses = unknownVisible
      ? [...newVisibleClasses, ...unknownClassIds]
      : newVisibleClasses;

    layer.filters = [{ type: 'value', field: 'CLASS_CODE', mode: 'include', values: combinedVisibleClasses }];
    layer.definitionExpression = `CLASS_CODE IN (${combinedVisibleClasses.join(',')})`;
  };

  useEffect(() => {
    // Cleanup sliders when their panels close
    if (!classifiedPanelOpen && classSliderRef.current) {
      classSliderRef.current.destroy();
      classSliderRef.current = null;
    }
    if (!outputPanelOpen && outputSliderRef.current) {
      outputSliderRef.current.destroy();
      outputSliderRef.current = null;
    }
    if (!inputPanelOpen && inputSliderRef.current) {
      inputSliderRef.current.destroy();
      inputSliderRef.current = null;
    }

    // Only proceed if at least one panel is open
    if (!classifiedPanelOpen && !outputPanelOpen && !inputPanelOpen) {
      return;
    }

    const updatePointCloudSize = (layer: PointCloudLayer, size: number) => {
      if (!layer.renderer) return;
      const r = (layer as any).renderer.clone();
      if (!r.pointSizeAlgorithm) {
        r.pointSizeAlgorithm = { type: 'fixed-size', size };
      } else {
        r.pointSizeAlgorithm.size = size;
      }
      layer.renderer = r;
    };

    // Small delay to ensure DOM is ready
    const timeout = setTimeout(() => {
      // Initialize INPUT slider if panel is open
      if (inputPanelOpen && !inputSliderRef.current) {
        const inputContainer = document.getElementById('input-point-size-slider');
        if (inputContainer) {
          // Clear any existing content
          inputContainer.innerHTML = '';

          inputSliderRef.current = new Slider({
            container: inputContainer,
            min: 1,
            max: 10,
            values: [inputPointSize],
            steps: 1,
            visibleElements: { rangeLabels: true },
          });

          inputSliderRef.current.on(['thumb-change', 'thumb-drag'], (e) => {
            const size = e.value;
            setInputPointSize(size);
            pointCloudLayersRef.current.forEach((l) => {
              if ((l as any).renderer?.type !== 'point-cloud-rgb') return;
              if (!l.loaded) {
                l.when(() => updatePointCloudSize(l, size));
              } else {
                updatePointCloudSize(l, size);
              }
            });
          });
        }
      }

      // Initialize OUTPUT slider if panel is open
      if (outputPanelOpen && !outputSliderRef.current) {
        const outputContainer = document.getElementById('output-point-size-slider');
        if (!outputContainer) return;

        // Clear any existing content
        outputContainer.innerHTML = '';

        const outputLayer = point3DLayerRef.current;
        if (!outputLayer) return;

        const outputRendererRef = { current: null as __esri.UniqueValueRenderer | null };

        const applySize = (size: number) => {
          if (!outputLayer?.loaded || !outputRendererRef.current || !outputSliderRef.current) return;

          const renderer = outputRendererRef.current;
          renderer.uniqueValueInfos.forEach((info) => {
            const sym = info.symbol as __esri.SimpleMarkerSymbol;
            if (sym) sym.size = size;
          });
          if (renderer.defaultSymbol) {
            (renderer.defaultSymbol as __esri.SimpleMarkerSymbol).size = size;
          }
          outputLayer.renderer = renderer.clone();
          outputLayer.refresh?.();
        };

        outputSliderRef.current = new Slider({
          container: outputContainer,
          min: 1,
          max: 20,
          values: [outputPointSize],
          steps: 1,
          visibleElements: { rangeLabels: true },
        });

        const initRenderer = () => {
          if (!outputLayer?.loaded) return;

          const r = outputLayer.renderer as __esri.UniqueValueRenderer;
          if (!r) return;

          outputRendererRef.current = r.clone();

          const currentSize =
            (r.uniqueValueInfos[0]?.symbol as __esri.SimpleMarkerSymbol)?.size ??
            (r.defaultSymbol as __esri.SimpleMarkerSymbol)?.size ??
            10;

          if (outputSliderRef.current) {
            outputSliderRef.current.values = [currentSize];
          }
          setOutputPointSize(currentSize);
        };

        if (outputLayer.loaded) {
          initRenderer();
        } else {
          outputLayer.when(initRenderer);
        }

        outputSliderRef.current.on(['thumb-change', 'thumb-drag'], (e) => {
          const size = e.value as number;
          setOutputPointSize(size);
          applySize(size);
        });
      }

      // Initialize CLASSIFIED slider if panel is open
      if (classifiedPanelOpen && !classSliderRef.current) {
        const classifiedContainer = document.getElementById('classified-point-size-slider');
        if (classifiedContainer) {
          // Clear any existing content
          classifiedContainer.innerHTML = '';

          classSliderRef.current = new Slider({
            container: classifiedContainer,
            min: 1,
            max: 10,
            values: [classifiedPointSize],
            steps: 1,
            visibleElements: { rangeLabels: true },
          });

          classSliderRef.current.on(['thumb-change', 'thumb-drag'], (e) => {
            const size = e.value;
            setClassifiedPointSize(size);
            pointCloudLayersRef.current.forEach((l) => {
              if ((l as any).renderer?.type !== 'point-cloud-unique-value') return;
              if (!l.loaded) {
                l.when(() => updatePointCloudSize(l, size));
              } else {
                updatePointCloudSize(l, size);
              }
            });
          });
        }
      }
    }, 50);

    return () => clearTimeout(timeout);
  }, [classifiedPanelOpen, outputPanelOpen, inputPanelOpen, classifiedPointSize, outputPointSize, inputPointSize]);

  // Sync panoControlsEnabled with panoFeatures availability
  useEffect(() => {
    if (!panoFeatures || panoFeatures.length === 0) {
      setPanoControlsEnabled(false);
      setPanoVisible(false);
    } else {
      // Enable controls when panoFeatures are available
      setPanoControlsEnabled(true);
    }
  }, [panoFeatures]);

  // Function to create cone geometry based on yaw, FOV, and center point
  const createConeGeometry = (
    center: __esri.Point,
    yaw: number, // heading angle in degrees (0° = North, 90° = East)
    fov: number, // horizontal field of view in degrees
    radius = 15,
  ) => {
    const startAngle = yaw - fov / 2;
    const endAngle = yaw + fov / 2;
    const numSegments = 20;
    const points: number[][] = [];

    points.push([center.x, center.y]);

    for (let i = 0; i <= numSegments; i++) {
      const angle = startAngle + (i * (endAngle - startAngle)) / numSegments;
      const rad = (angle * Math.PI) / 180;

      // Adjust for map coordinates: 0° = North
      const x = center.x + radius * Math.sin(rad);
      const y = center.y + radius * Math.cos(rad);

      points.push([x, y]);
    }

    points.push([center.x, center.y]); // close polygon

    return {
      type: 'polygon',
      rings: [points],
      spatialReference: center.spatialReference,
    } as __esri.PolygonProperties;
  };
  // Function to update cone geometry based on yaw, FOV, and center point
  const updateConeGeometry = (yaw: number, hfov: number) => {
    if (!centerPointRef.current || !coneGraphicRef.current) return;

    const totalYaw = featureKappaRef.current + yaw;
    const newGeom = createConeGeometry(centerPointRef.current, totalYaw, hfov);
    coneGraphicRef.current.geometry = newGeom;
  };
  // Mouse Interaction handler functions (handlePanoMouseDown, handlePanoMouseUp) for pano viewer
  const handlePanoMouseDown = () => {
    const yaw = ReactPannellum.getYaw();
    const hfov = ReactPannellum.getHfov();
    // update cone wrt user interaction on the Pano viewer
    updateConeGeometry(yaw, hfov);
  };

  const handlePanoMouseUp = () => {
    const yaw = ReactPannellum.getYaw();
    const hfov = ReactPannellum.getHfov();
    // update cone wrt user interaction on the Pano viewer
    updateConeGeometry(yaw, hfov);
  };
  function bearing(from: __esri.Point, to: __esri.Point) {
    const dx = to.x - from.x;
    const dy = to.y - from.y;
    let angle = (Math.atan2(dx, dy) * 180) / Math.PI;
    if (angle < 0) angle += 360;
    return angle;
  }
  async function proximityPoints(
    imageName: string,
    graphic: __esri.Graphic,
    geojsonLayer: __esri.FeatureLayer,
  ) {
    if (!projectOperator.isLoaded()) await projectOperator.load();

    const webMercatorSR = new SpatialReference({ wkid: 3857 });

    try {
      const results = await geojsonLayer.queryFeatures();
      const nearbyFeatures = results.features.filter((feature) => {
        if (feature.attributes.Image_Filename === imageName) return false;

        const geom1 = projectOperator.execute(graphic.geometry, webMercatorSR);
        const geom2 = projectOperator.execute(feature.geometry, webMercatorSR);

        const dist = distanceOperator.execute(geom1, geom2, 'meters');
        return dist < 5;
      });
      return nearbyFeatures;
    } catch (err) {
      console.error('Error querying features:', err);
      return [];
    }
  }

  async function addNearbyHotspots(
    currentFeature: __esri.Graphic,
    _panoFeatures: __esri.Graphic[],
    panoLayer: __esri.FeatureLayer,
    currentSceneId: string,
  ) {
    const currentPoint = currentFeature.geometry as __esri.Point;
    const currentYaw = currentFeature.attributes.kappa ?? 0;
    const currentFilename = currentFeature.attributes.filename;

    // Query all features (for next/previous)
    const allFeaturesQuery = await panoLayer.queryFeatures({
      where: '1=1',
      outFields: ['*'],
      returnGeometry: true,
    });

    // sort features based on Pano Image time of capture
    const allFeatures = allFeaturesQuery.features.sort(
      (a, b) =>
        new Date(a.attributes.datetimeoriginal).getTime() -
        new Date(b.attributes.datetimeoriginal).getTime(),
    );

    // Finding next and previous based on index
    const currentFeatureIndex = allFeatures.findIndex(
      (f) => f.attributes.filename === currentFilename,
    );
    const previousFeature =
      currentFeatureIndex > 0 ? allFeatures[currentFeatureIndex - 1] : null;
    const nextFeature =
      currentFeatureIndex < allFeatures.length - 1
        ? allFeatures[currentFeatureIndex + 1]
        : null;

    // Store refs for later use in keyboard navigation
    panoPrevFeatureRef.current = previousFeature;
    panoNextFeatureRef.current = nextFeature;

    // Nearby points excluding next/previous
    const nearby = await proximityPoints(currentFilename, currentFeature, panoLayer);
    const filteredNearby = nearby.filter((p) => {
      const fn = p.attributes.filename;
      return (
        fn !== currentFeature.attributes.filename &&
        fn !== previousFeature?.attributes.filename &&
        fn !== nextFeature?.attributes.filename
      );
    });

    // Compute direction and distance from current point to each nearby feature
    const directionInfo = filteredNearby.map((p) => {
      const point = p.geometry as __esri.Point;
      const angle = bearing(currentPoint, point); // angle between current point and target point
      let relative = angle - currentYaw; // Normalize North wrt current yaw
      if (relative > 180) relative -= 360;
      if (relative < -180) relative += 360;

      // Compute distance (in meters)
      const geom1 = projectOperator.execute(currentPoint, new SpatialReference({ wkid: 3857 }));
      const geom2 = projectOperator.execute(point, new SpatialReference({ wkid: 3857 }));
      const dist = distanceOperator.execute(geom1, geom2, 'meters');

      const z1 = currentPoint.z ?? 0;
      const z2 = point.z ?? 0;
      const dz = z2 - z1;
      const pitch = Math.atan2(dz, dist) * (180 / Math.PI);
      return { feature: p, relative, distance: dist, pitch };
    });

    // consider the points within 30 degree to 150 degree in left/right direction
    const angleThreshold = 30;
    const lateralCandidates = directionInfo.filter(
      (d) =>
        Math.abs(d.relative) > angleThreshold &&
        Math.abs(d.relative) < 180 - angleThreshold,
    );

    // Sort left and right by distance first, then by absolute relative angle
    const left = lateralCandidates
      .filter((d) => d.relative < 0)
      .sort((a, b) => a.distance - b.distance || Math.abs(a.relative) - Math.abs(b.relative))[0];

    const right = lateralCandidates
      .filter((d) => d.relative > 0)
      .sort((a, b) => a.distance - b.distance || Math.abs(a.relative) - Math.abs(b.relative))[0];

    const sceneData = [
      { feature: nextFeature, id: 'nextScene', yaw: 0, pitch: -20, text: 'Next' },
      { feature: previousFeature, id: 'previousScene', yaw: 180, pitch: -130, text: 'Previous' },
      { feature: left?.feature, id: 'leftScene', yaw: -40, pitch: -30, text: 'Left' },
      { feature: right?.feature, id: 'rightScene', yaw: 40, pitch: -30, text: 'Right' },
    ];

    for (const s of sceneData) {
      if (!s.feature) continue; // skip if no feature

      const { filepath, filename } = s.feature.attributes;

      if (!filepath || !filename) {
        console.warn(`Skipping hotspot ${s.id}: missing filepath or filename`);
        continue;
      }

      try {
        const sceneId = `scene_${filename}`;
        const signedUrl = await dispatch(getSignedURLThunk(filepath));
        if (!signedUrl) {
          console.warn(`Skipping hotspot ${s.id}: failed to fetch signed URL`);
          continue;
        }

        // Add neighbor pano scene
        ReactPannellum.addScene(sceneId, {
          type: 'equirectangular',
          imageSource: signedUrl,
          autoLoad: true,
        });

        // Add hotspot to current scene
        ReactPannellum.addHotSpot(
          {
            id: `${s.id}_${filename}`,
            pitch: s.pitch ?? 0,
            yaw: s.yaw,
            type: 'scene',
            text: s.text,
            cssClass: `pano-icon-${s.id}`,
            clickHandlerFunc: async () => {
              if (!lasViewRef.current) return;

              // Load the new panorama scene
              await loadPanoScene(s.feature, panoLayerFeatureRef.current, sceneId);
            },
            sceneId,
          },
          currentSceneId,
        );
      } catch (err) {
        console.error(`Error processing hotspot ${s.id}:`, err);
        continue; // skip this hotspot and continue
      }
    }
  }

  // Reusable function to loads a pano, cone and hotspots
  async function loadPanoScene(
    feature: __esri.Graphic,
    panoLayer: __esri.FeatureLayer,
    targetSceneId: string = 'firstScene',
  ) {
    try {
      if (!lasViewRef.current) return;
      setPanoError(null); // clear the pano error everytime scene loads
      setPanoWarning(null); // clear the pano warning everytime scene loads
      const { filepath, filename, kappa, remarks } = feature.attributes;

      if (!filepath) {
        throw new Error('Missing required attribute: filepath');
      }

      if (!filename) {
        throw new Error('Missing required attribute: filename');
      }

      if (kappa === undefined || kappa === null) {
        setPanoWarning('Panorama direction may be inaccurate');
      }
      const finalKappa = kappa ?? 0; // uses 0 only if kappa is null or undefined

      if (remarks) {
        setPanoWarning('If the Pano image is not visible');
      }

      // Clean old cone
      if (coneGraphicRef.current) {
        lasViewRef.current.graphics.remove(coneGraphicRef.current);
        coneGraphicRef.current = null;
      }

      // Project to 3857
      const webMercatorSR = new SpatialReference({ wkid: 3857 });
      if (!projectOperator.isLoaded()) await projectOperator.load();

      const projectedCenter = projectOperator.execute(
        feature.geometry as __esri.Point,
        webMercatorSR,
      ) as __esri.Point;

      lasViewRef.current.goTo(
        {
          target: projectedCenter,
        },
      );

      centerPointRef.current = projectedCenter;
      featureKappaRef.current = finalKappa;

      // Draw cone
      const hfov = ReactPannellum.getHfov() ?? 90;
      const coneGeom = createConeGeometry(projectedCenter, finalKappa, hfov);

      const coneGraphic = new Graphic({
        geometry: coneGeom,
        symbol: {
          type: 'simple-fill',
          color: [255, 0, 0, 0.55],
          outline: null,
        },
      });

      lasViewRef.current.graphics.add(coneGraphic);
      coneGraphicRef.current = coneGraphic;
      currentFeatureRef.current = feature;

      // Get presigned URL
      const signedUrl = await dispatch(getSignedURLThunk(filepath));
      const objectId = feature?.attributes?.object_id;

      if (!signedUrl) {
        // keep a track on failed pano features
        if (objectId) {
          setFailedPanoIds((prev) =>
            (prev.includes(objectId) ? prev : [...prev, objectId]));
        }
        setPanoErrorClosed(false);
        setPanoWarning(null);
        setImageSource(signedUrl);
        setPanoError('Error loading Pano Image');
        console.warn(`Skipping scene ${filename} because signed URL is missing`);
        return; // Early return if no signed URL
      }

      // Successfully got signed URL - remove from failed list if it was there
      if (objectId) {
        setFailedPanoIds((prev) => prev.filter((id) => id !== objectId));
      }

      // set image source
      setImageSource(signedUrl);
      currentSceneIdRef.current = targetSceneId === 'firstScene' ? 'firstScene' : `scene_${filename}`;
    } catch (error) {
      console.error('Error in loadPanoScene:', error);
    }
  }

  // set pano hotspots after pano is loaded using useState function
  useEffect(() => {
    if (!panoVisible) return;

    let interval: any;

    interval = setInterval(async () => {
      if (ReactPannellum.isLoaded()) {
        clearInterval(interval);
        if (currentFeatureRef.current && panoLayerFeatureRef.current) {
          await addNearbyHotspots(
            currentFeatureRef.current,
            panoFeatures,
            panoLayerFeatureRef.current,
            'firstScene',
          );
        }
      }
    }, 50);

    return () => clearInterval(interval);
  }, [imageSource, panoVisible]);

  // Pano loading error
  useEffect(() => {
    if (!panoVisible || !panoRef.current) return;

    const panoDiv = panoRef.current;

    const observer = new MutationObserver(() => {
      const errorEl = panoDiv.querySelector(
        '.pnlm-error-msg, .pnlm-info-box',
      ) as HTMLElement | null;

      if (errorEl && errorEl.innerText.trim()) {
        setPanoErrorClosed(false);
        setPanoWarning(null);
        setPanoError('Error loading Pano Image');
      }
    });

    observer.observe(panoDiv, {
      childList: true,
      subtree: true,
    });

    return () => observer.disconnect();
  }, [panoVisible]);

  // pano keyboard navigation shortcut listener
  useEffect(() => {
    if (!panoVisible || !panoRef.current) return;

    const handleKeyDown = async (e: KeyboardEvent) => {
      // Ignore when typing in input fields
      const tag = (e.target as HTMLElement)?.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA') return;

      if (e.key === 'ArrowUp') {
        e.preventDefault();
        await loadPanoScene(
          panoNextFeatureRef.current,
          panoLayerFeatureRef.current,
          'firstScene',
        );
      }

      if (e.key === 'ArrowDown') {
        e.preventDefault();
        await loadPanoScene(
          panoPrevFeatureRef.current,
          panoLayerFeatureRef.current,
          'firstScene',
        );
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [panoVisible]);

  const getHas3dForSiteId = useCallback((siteId: string): boolean => {
    if (!orders || !Array.isArray(orders)) return false;

    for (const order of orders) {
      if (!order.cadFiles || !Array.isArray(order.cadFiles)) continue;

      const cadFile = order.cadFiles.find((cad: any) => cad._id === siteId);
      if (cadFile && typeof cadFile.has3d === 'boolean') {
        return cadFile.has3d;
      }
    }

    return false; // default to on-the-ground
  }, [orders]);

  const getElevationInfo = (siteId: string): ElevationInfo => {
    const has3d = getHas3dForSiteId(siteId);
    const lasFile = projectData?.lasFiles?.find((f: any) => f._id === siteId);
    // Get the offset value if it exists - vj praba
    const offset = lasFile?.esriOffsetZ ?? 0;
    const info: ElevationInfo = {
      mode: has3d ? 'absolute-height' : 'on-the-ground',
      ...(offset !== 0 && { offset }),
    };
    return info;
  };

  const extractColorFromRenderer = (drawingInfo: any): string => {
    try {
      if (!drawingInfo || !drawingInfo.renderer) {
        return '#0078D4'; // Default ArcGIS blue
      }

      const { renderer } = drawingInfo;

      // Simple Renderer
      if (renderer.type === 'simple' && renderer.symbol) {
        const { symbol } = renderer;

        // For point symbols
        if (symbol.color && Array.isArray(symbol.color)) {
          const [r, g, b, a = 255] = symbol.color;
          return `rgba(${r}, ${g}, ${b}, ${a / 255})`;
        }

        // For line symbols
        if (symbol.outline && symbol.outline.color && Array.isArray(symbol.outline.color)) {
          const [r, g, b, a = 255] = symbol.outline.color;
          return `rgba(${r}, ${g}, ${b}, ${a / 255})`;
        }
      }

      // Unique Value Renderer - use first unique value's color
      if (renderer.type === 'uniqueValue' && renderer.uniqueValueInfos && renderer.uniqueValueInfos.length > 0) {
        const firstInfo = renderer.uniqueValueInfos[0];
        if (firstInfo.symbol && firstInfo.symbol.color && Array.isArray(firstInfo.symbol.color)) {
          const [r, g, b, a = 255] = firstInfo.symbol.color;
          return `rgba(${r}, ${g}, ${b}, ${a / 255})`;
        }
      }

      // Class Breaks Renderer - use first class break's color
      if (renderer.type === 'classBreaks' && renderer.classBreakInfos && renderer.classBreakInfos.length > 0) {
        const firstInfo = renderer.classBreakInfos[0];
        if (firstInfo.symbol && firstInfo.symbol.color && Array.isArray(firstInfo.symbol.color)) {
          const [r, g, b, a = 255] = firstInfo.symbol.color;
          return `rgba(${r}, ${g}, ${b}, ${a / 255})`;
        }
      }

      return '#0078D4'; // Default color if no valid color found
    } catch (err) {
      console.warn('Error extracting color from renderer:', err);
      return '#0078D4';
    }
  };

  useEffect(() => {
    orderref.current = selectedOrderId;
    if (!selectedOrderId || !lasViewRef.current) return;

    const selectedOrder = sortedPlacedOrders.find((o) => o._id === selectedOrderId);
    if (!selectedOrder) return;
    if (
      selectedOrder?.middlePoint &&
      selectedOrder.middlePoint.latitude != null &&
      selectedOrder.middlePoint.longitude != null
    ) {
      const view = lasViewRef.current;
      reactiveUtils.whenOnce(() => view.ready).then(() => {
        if (!view) return;
        view.goTo(
          {
            target: {
              longitude: selectedOrder.middlePoint.longitude,
              latitude: selectedOrder.middlePoint.latitude,
            },
            distance: 5000,
            heading: 0,
            tilt: 0,
          },
          {
            duration: 1500,
          },
        ).catch((e) => {
          if (e.name !== 'AbortError') {
            console.error(e);
          }
        });
      });
    }
  }, [selectedOrderId]);
  useEffect(() => {
    let active = true;
    let lasViewToCleanup: __esri.SceneView | null = null;
    const urlsToRevoke: string[] = [];

    // Wait for token registration to complete before creating layers
    if (!tokenRegistered || !lasRef.current) return;
    // Determine the initial order to load
    const getInitialOrder = () => {
      if (!sortedPlacedOrders.length) return null;
      return sortedPlacedOrders[0];
    };

    const initialOrder = getInitialOrder();
    const initialOrderId = initialOrder?._id || selectedOrderId;

    const orderUnits = initialOrder?.cadFiles?.find((cad: any) => cad.units)?.units || 'meter';
    const isUSFoot = orderUnits === 'US survey foot';

    // If no order is selected yet, set the initial order
    if (!selectedOrderId && initialOrderId) {
      dispatch(SetOrderSelectedAction(initialOrderId));
    }
    const cadIds = initialOrder?.cadFiles?.map((cad: any) => cad._id) || [];
    setorderid(selectedOrderId);
    orderref.current = selectedOrderId;

    const initializeLayers = async () => {
      // get the combined extent

      const lasMap = new Map({
        basemap: 'streets',
        ground: 'world-elevation',
        layers: [],
      });
      lasMapInstanceRef.current = lasMap;

      const lasView = new SceneView({
        container: lasRef.current,
        center: viewportCenter.center,
        zoom: viewportCenter.zoom,
        map: lasMap,
      });

      if (!active) {
        lasView.destroy();
        return;
      }
      lasViewToCleanup = lasView;
      lasViewRef.current = lasView;
      (window as any).lasView = lasView;
      setlasView(lasView);
      lasView.ui.remove('zoom');
      lasView.ui.remove('navigation-toggle');
      lasView.ui.remove('compass');
      const zoomWidget_las = new Zoom({
        view: lasView,
      });
      const navigationToggle_las = new NavigationToggle({
        view: lasView,
      });
      const compass_las = new Compass({
        view: lasView,
      });
      lasView.ui.add(zoomWidget_las, 'top-right');
      lasView.ui.add(navigationToggle_las, 'top-right');
      lasView.ui.add(compass_las, 'top-right');
      const basemapWidget = new Basemap({
        view: lasView,

      });
      basemapRef.current = basemapWidget;
      lasView.ui.add(basemapWidget, 'top-right');

      const distanceMeasurement3d = new DirectLineMeasurement3D({
        view: lasView,
      });
      const areaMeasurement3d = new AreaMeasurement3D({
        view: lasView,
      });
      const lasdistance3d = new DistanceMeasurement({
        view: lasView,
        measurement: distanceMeasurement3d,
      });
      lasView.ui.add(lasdistance3d, 'top-right');
      setDistanceWidget3d(lasdistance3d);
      const lasArea3d = new AreaMeasurement({
        view: lasView,
        measurement: areaMeasurement3d,
      });
      lasView.ui.add(lasArea3d, 'top-right');
      setAreaWidget3d(lasArea3d);
      const lasTerrain = new TerrainWidget({
        view: lasView,
      });
      terrainRef.current = lasTerrain;
      lasTerrain.on('terrain-toggle', ({ active }) => {
        const terrainOff = !!active;
        toggleTerrain([{ map: lasMapInstanceRef.current, name: 'LAS Map' }], terrainOff);
      });
      lasView.on('click', async (event) => {
        islasViewRef.current = true;
        if (measuringRefDistance.current || measuringRefArea.current) return;
        // annotation click event in 3D Viewer
        const screenPoint = lasView.toScreen(event.mapPoint);
        const lasRect = lasView.container.getBoundingClientRect();
        const globalX = screenPoint.x;
        const globalY = screenPoint.y;
        const x = Math.min(Math.max(globalX, 0), lasRect.width);
        const y = Math.min(Math.max(globalY, 0), lasRect.height);
        setPopupXY({ x, y });
        const EXCLUDED = new Set([
          'objectid', 'OBJECTID', 'gid', 'origin_epsg', 'srid', 'site_id',
          'local_layer_id', 'timestamp', 'block_def_id', 'shape', 'SHAPE',
          'Shape__Area', 'Shape__Length', 'has3d', 'layer_id', 'outer_p',
          'OUTER_P', 'Outer_P',
        ]);

        // Collect all sublayers from geodatabase groups for precise hit-testing
        const gdbSublayers = (geodatabaseLayersRef.current || []).flatMap((group) => {
          if (group.layers) {
            return (group.layers as any).toArray();
          }
          return [group];
        });

        // Collect all sublayers from shapefile groups for precise hit-testing
        const shpSublayers = (shapefileLayersRef.current || []).flatMap((group) => {
          if (group.layers) {
            return (group.layers as any).toArray();
          }
          return [group];
        });

        // HitTest on Geodatabase and Pano layers
        const hit = await lasView.hitTest(event, {
          include: [...gdbSublayers, ...shpSublayers, panoLayerFeatureRef.current].filter(Boolean),
        });

        if (hit.results.length > 0) {
          const graphicResults = hit.results.filter((r) => {
            const isGeoJSON = r.layer.type === "geojson";
            const isPano = r.layer.title === "Pano Features";
            const isKML = r.layer.title === "KML Features";

            return isGeoJSON && !isPano && !isKML;
          });
          if (graphicResults.length > 0) {
            const features = await Promise.all(
              graphicResults.map(async (r) => {
                const { graphic } = r;
                const layer = graphic.layer as FeatureLayer;
                const objectId = graphic.attributes?.OBJECTID ?? graphic.attributes?.objectid;

                const geomType = (layer.geometryType || '').replace('esriGeometry', '').toLowerCase();

                let attrs = { ...graphic.attributes };
                let { geometry } = graphic;
                try {
                  const result = await layer.queryFeatures({
                    objectIds: [objectId],
                    outFields: ['*'],
                    returnGeometry: true,
                    outSpatialReference: lasView.spatialReference,
                  });
                  if (result.features.length > 0) {
                    attrs = { ...result.features[0].attributes };
                    if (result.features[0].geometry) {
                      geometry = result.features[0].geometry;
                    }
                  }
                } catch (err) {
                  console.warn('queryFeatures failed, using hitTest attrs', err);
                }

                const layerIdStr = String(attrs.layer_id ?? attrs.LAYER_ID ?? '');
                const meta = featureListRef.current.find((f) => String(f.value) === layerIdStr);
                // Prioritize layer-level metadata (set for GDB) over feature-level layer_id lookup
                const layerName = (layer as any).layerName || meta?.label || layer.title || 'Feature';
                const layerColor = (layer as any).layerColor || meta?.color || '#cccccc';

                // ── Shape metric helpers (matching old showPopupInfo conversions) ──
                const Area_Shape = (value: number) => Number((value * 10000000000).toFixed(4));
                const Length_Shape = (value: number) => Number((value * 100000).toFixed(4));

                const display: Record<string, any> = {};

                // ── Fields shown in old popup (EPSG Unit, Geometry Type, Given EPSG, Outer P) ──

                display['Geometry Type'] = `ST_${geomType.charAt(0).toUpperCase() + geomType.slice(1)}`; // e.g. ST_Polygon
                if (attrs.origin_epsg != null && attrs.origin_epsg !== '') {
                  display['Given EPSG'] = attrs.origin_epsg;
                }

                // ── Shape Area / Shape Length (same conversions as old code) ──
                if (geomType === 'polygon') {
                  const rawArea = attrs.Shape__Area ?? attrs['st_area(shape)'];
                  const rawPeri = attrs.Shape__Length ?? attrs['st_perimeter(shape)'];
                  if (rawArea != null) display['Shape Area'] = `${Area_Shape(rawArea)} sq m`;
                  if (rawPeri != null) display['Shape Length'] = `${Length_Shape(rawPeri)} m`;
                  const outerP =
                    attrs.outer_p ??
                    attrs.OUTER_P ??
                    attrs.Outer_P;

                  if (outerP != null && outerP !== '') { display['Outer P'] = outerP; }
                } else if (geomType === 'polyline') {
                  const rawLen = attrs.Shape__Length ?? attrs['st_length(shape)'];
                  if (rawLen != null) display['Shape Length'] = `${Length_Shape(rawLen)} m`;
                }

                // ── Remaining non-excluded fields (dynamic, same as before) ──
                const ALREADY_SHOWN = new Set([
                  'origin_epsg', 'Outer P', 'OUTER_P',
                  'st_area(shape)', 'st_perimeter(shape)', 'st_length(shape)',
                ]);
                Object.entries(attrs).forEach(([key, val]) => {
                  if (EXCLUDED.has(key)) return;
                  if (ALREADY_SHOWN.has(key)) return;
                  if (val === undefined || val === null || val === '') return;
                  display[key] = val;
                });

                // Internal keys for PopupWindow header
                display.layerName = layerName;
                display.layerColor = layerColor;
                display.geometry = geometry;

                return { display, geometry, geomType };
              }),
            );

            const validFeatures = features.filter(Boolean);

            if (validFeatures.length > 0) {
              const highlightFeature = (index: number) => {
                highlightLayer.removeAll();
                const { geometry, geomType } = validFeatures[index] || validFeatures[0];
                if (!geometry) return;
                let highlightSymbol: any;
                if (geomType === 'polygon') {
                  highlightSymbol = { type: 'simple-fill', color: [0, 255, 255, 0.2], outline: { color: [0, 255, 255, 1], width: 2 } };
                } else if (geomType === 'polyline') {
                  highlightSymbol = { type: 'simple-line', color: [0, 255, 255, 1], width: 3 };
                } else {
                  highlightSymbol = { type: 'simple-marker', color: [0, 255, 255, 1], size: 12, outline: { color: [255, 255, 255], width: 1 } };
                }
                highlightLayer.add(new Graphic({ geometry, symbol: highlightSymbol }));
              };

              (window as any).__highlightFeature = highlightFeature;
              highlightFeature(0);
              setFilteredFeatures(validFeatures.map((f) => f.display));
              setCurrentIndex(0);
              setShowPopup(true);
              SetPopupVisibility(true);
            }
          } else {
            setShowPopup(false);
            highlightLayer.removeAll();
          }
          // Find the hit result that belongs to panoLayer
          const result = hit.results.find((r) => (panoLayerFeatureRef.current && r.graphic.layer === panoLayerFeatureRef.current));
          if (!result) return; // Exit if the click wasn't on panoLayer
          const feature = result.graphic;
          // go to the new feature point
          lasView.goTo(
            { center: feature.geometry },
            { duration: 400 },
          );
          await loadPanoScene(feature, panoLayerFeatureRef.current, 'firstScene');
        } else {
          setShowPopup(false);
        }
      });

      lasMapInstanceRef.current = lasMap;

      // Define MapImageLayers for point, line, and polygon
      const mapimagelayer = new MapImageLayer({
        url: config.layerServiceUrl,
        title: 'mapimagelayer',
        sublayers: [{ id: 0, visible: true },
          { id: 1, visible: true },
          { id: 2, visible: true },
          { id: 3, visible: true }],
      });
      setMapImageLayer(mapimagelayer);
      const layerPoint = mapimagelayer.findSublayerById(1);
      const layerLine = mapimagelayer.findSublayerById(2);
      const layer = mapimagelayer.findSublayerById(3);
      const layerText = mapimagelayer.findSublayerById(0);

      //  layer URLs and query URL based on units
      const layersQueryUrl = isUSFoot ? config.layersUrlFt : config.layersUrl;
      const text3DLayerUrl = isUSFoot ? config.text3DftLayer : config.text3Dlayer;
      const point3DLayerUrl = isUSFoot ? config.point3dftLayer : config.point3dLayer;
      const polyline3DLayerUrl = isUSFoot ? config.polyline3dftlayer : config.polyline3dLayer;
      const polygon3DLayerUrl = isUSFoot ? config.polygon3dftLayer : config.polygon3dLayer;

      const polylineLayer = new FeatureLayer({
        url: polyline3DLayerUrl,
        title: '3D_Polyline',
      });

      const polygonLayer = new FeatureLayer({
        url: polygon3DLayerUrl,
        title: '3D_Polygon',
      });

      const text3Dlayer = new FeatureLayer({
        url: text3DLayerUrl,
        title: '3D_Text',
        renderer: {
          type: 'simple',
          symbol: {
            type: 'simple-marker',
            size: 0, // Make points invisible
            color: [0, 0, 0, 0], // Transparent
            outline: {
              width: 0,
            },
          },
        },
      });

      const point3DLayer = new FeatureLayer({
        url: point3DLayerUrl,
        title: '3D_Point',
      });
      point3DLayerRef.current = point3DLayer;

      const featureLayersArray = [polylineLayer, polygonLayer, point3DLayer, text3Dlayer];
      setFeatureLayers(featureLayersArray);

      const highlightLayer = new GraphicsLayer({
        id: 'highlight-layer',
      });
      lasMap.add(highlightLayer);

      const addWarning = (message: string) => {
        setWarnings((prev) => [...prev, message]);
      };

      (window as any).mapimagelayer = mapimagelayer;
      (window as any).layerPoint = layerPoint;
      (window as any).layerLine = layerLine;
      (window as any).layer = layer;
      (window as any).layerText = layerText;
      (window as any).polylineLayer = polylineLayer;
      (window as any).polygonLayer = polygonLayer;
      (window as any).point3DLayer = point3DLayer;
      (window as any).text3Dlayer = text3Dlayer;

      // FeatureLayer for zooming
      const fl = new FeatureLayer({
        outFields: ['*'],
        url: config.fl,
      });
      const flText = new FeatureLayer({
        outFields: ['*'],
        url: `${config.layerServiceUrl}/0`,
      });
      const flPoint = new FeatureLayer({
        outFields: ['*'],
        url: `${config.layerServiceUrl}/1`,
      });
      const flLine = new FeatureLayer({
        outFields: ['*'],
        url: `${config.layerServiceUrl}/2`,
      });
      fl.definitionExpression = `site_id = '${config.site_id}'`;
      // Query layers table and apply rendering
      const queryLayersTable = (siteId: string, has3D: boolean): void => {
        const params = new Query({
          returnGeometry: false,
          returnDistinctValues: true,
          outFields: config.layersoutFields_Arr,
          where: `site_id = '${siteId}'`,
        });
        query
          .executeQueryJSON(layersQueryUrl, params)
          .then((response: any) => {
            const uniqueValues: UniqueValue[] = response.features.map((feature: any) => ({
              value: feature.attributes.id?.toString() || '',
              color: feature.attributes[config.layersoutFields.color] || 'rgba(0,0,0,1)',
              linetype: feature.attributes[config.layersoutFields.line_type] || 'CONTINUOUS',
              linewidth: feature.attributes[config.layersoutFields.line_width] ?? 1,
              label: feature.attributes[config.layersoutFields.name] || 'Unknown Layer',
              font_size: feature.attributes[config.textoutFields.font_size] || 6,
            }));

            // Save to state for custom layer list UI
            setFeatureList(uniqueValues);
            const initialChecked: { [key: string]: boolean } = {};
            uniqueValues.forEach((item) => {
              initialChecked[item.value] = true;
            });
            setCheckedItems(initialChecked);

            // Apply rendering for points, lines, and polygons
            applyRenderingToAllSublayers(mapimagelayer, uniqueValues);
            if (has3D) {
              manageFeatureLayerLine(uniqueValues, polylineLayer);
              manageFeatureLayerPolygon(uniqueValues, polygonLayer);
              manageFeatureLayerPoint(uniqueValues, point3DLayer);
              manageFeatureLayerText(uniqueValues, text3Dlayer);
            }
          })
          .catch((err: Error) => console.error('Layers table query failed:', err));
      };
      (window as any).queryLayersTable = queryLayersTable;

      const checkfor3dfeature = async (siteId: string): Promise<boolean> => {
        const params = new Query({
          returnGeometry: false,
          outFields: config.has3dField,
          where: `site_id = '${siteId}'`,
          groupByFieldsForStatistics: config.has3dField,
          outStatistics: config.has3dStatistics,
        });

        const queryurl = config.has3dCountUrl;
        try {
          const response: any = await query.executeQueryJSON(queryurl, params);

          return response.features.some((feature: any) => {
            const attrs = feature.attributes;
            return attrs.has3d === 1 && attrs.count > 0;
          });
        } catch (err) {
          console.error('Layers table query failed:', err);
          return false;
        }
      };
      (window as any).checkfor3dfeature = checkfor3dfeature;

      const manageFeatureLayerText = (
        uniqueValues: UniqueValue[],
        layer: FeatureLayer,
      ): void => {
        if (!uniqueValues.length) {
          return;
        }
        layer.renderer = {
          type: 'simple',
          symbol: {
            type: 'simple-marker',
            size: 0,
            color: [0, 0, 0, 0],
            outline: { width: 0 },
          },
        };
        // Create LabelClasses for each unique value
        const labelClasses: LabelClass[] = uniqueValues.map(
          (item) =>
            new LabelClass({
              where: `layer_id = '${item.value}'`, // filter for each unique value
              labelExpressionInfo: { expression: '$feature.text' }, // your text field
              symbol: {
                type: 'text',
                color: item.color,
                font: {
                  size: item.font_size,
                  family: 'Arial',
                  weight: 'bold',
                },
                haloColor: 'white',
                haloSize: 0,
              },
              labelPlacement: 'above-center',
            }),
        );
        layer.labelingInfo = labelClasses;
        layer.labelsVisible = true;
        if (!lasMap.layers.includes(layer)) {
          lasMap.add(layer);
        }
      };

      const manageFeatureLayerLine = (
        uniqueValues: UniqueValue[],
        layer: FeatureLayer,
      ): void => {
        if (!uniqueValues.length) {
          return;
        }

        const rendererInfos: __esri.UniqueValueRenderer['uniqueValueInfos'] = uniqueValues.map(
          (item) => ({
            value: item.value,
            symbol: {
              type: 'simple-line',
              style: config.dashPatterns[item.linetype?.toUpperCase()] || 'solid',
              color: item.color,
              width:
                item.linewidth === 0
                  ? 0.5
                  : [-1, -2, -3].includes(item.linewidth)
                    ? 1
                    : item.linewidth > 0
                      ? item.linewidth / 5
                      : 0.1,
            },
            label: item.label,
          }),
        );

        layer.renderer = {
          type: 'unique-value',
          field: 'layer_id',
          uniqueValueInfos: rendererInfos,
        };
        if (!lasMap.layers.includes(layer)) {
          lasMap.add(layer);
        }
      };

      const manageFeatureLayerPolygon = (
        uniqueValues: UniqueValue[],
        layer: FeatureLayer,
      ): void => {
        if (!uniqueValues.length) {
          return;
        }

        const rendererInfos: __esri.UniqueValueRenderer['uniqueValueInfos'] = uniqueValues.map(
          (item) => ({
            value: item.value,
            symbol: {
              type: 'simple-fill',
              color: [0, 0, 0, 0],
              style: 'none',
              outline: {
                type: 'simple-line',
                style: config.dashPatterns[item.linetype?.toUpperCase()] || 'solid',
                color: item.color,
                width: item.linewidth === 0 ? 0.5 : [-1, -2, -3].includes(item.linewidth) ? 1 : item.linewidth > 0 ? item.linewidth / 5 : 0.1,
              },
            },
            label: item.label,
          }),
        );

        layer.renderer = {
          type: 'unique-value',
          field: 'layer_id',
          uniqueValueInfos: rendererInfos,
        };

        lasMap.add(polygonLayer);
      };

      const manageFeatureLayerPoint = (
        uniqueValues: UniqueValue[],
        layer: FeatureLayer,
      ): void => {
        if (!uniqueValues.length) {
          return;
        }

        const rendererInfos: __esri.UniqueValueRenderer['uniqueValueInfos'] = uniqueValues.map(
          (item) => ({
            value: item.value,
            symbol: {
              type: 'simple-marker',
              style: 'circle',
              size: 5,
              color: item.color, // Transparent fill
              outline: {
                type: 'simple-line',
                style: config.dashPatterns[item.linetype?.toUpperCase()] || 'solid',
                color: item.color,
                width: item.linewidth === 0 ? 0.5 : [-1, -2, -3].includes(item.linewidth) ? 1 : item.linewidth > 0 ? item.linewidth / 5 : 0.1,
              },
            },
            label: item.label,
          }),
        );

        layer.renderer = {
          type: 'unique-value',
          field: 'layer_id',
          uniqueValueInfos: rendererInfos,
        };
        lasMap.add(layer);
      };

      const applyRenderingToAllSublayers = (
        mapimagelayer: MapImageLayer,
        uniqueValues: UniqueValue[],
      ): void => {
        if (!uniqueValues.length) return;

        // Get all sublayers
        const layerText = mapimagelayer.findSublayerById(0);
        const layerPoint = mapimagelayer.findSublayerById(1);
        const layerLine = mapimagelayer.findSublayerById(2);
        const layerPolygon = mapimagelayer.findSublayerById(3);

        // Apply Text Labels (sublayer 0)
        if (layerText) {
          const labelClasses: LabelClass[] = uniqueValues.map(
            (item) =>
              new LabelClass({
                where: `layer_id = '${item.value}'`,
                labelExpressionInfo: { expression: '$feature.text' },
                symbol: {
                  type: 'text',
                  color: item.color,
                  font: {
                    size: item.font_size,
                    family: 'Arial',
                    weight: 'bold',
                  },
                  haloColor: 'white',
                  haloSize: 1,
                },
                labelPlacement: 'above-center',
              }),
          );
          layerText.labelsVisible = true;
          layerText.labelingInfo = labelClasses;
        }

        // Apply Point Renderer (sublayer 1)
        if (layerPoint) {
          layerPoint.renderer = {
            type: 'unique-value',
            field: 'layer_id',
            uniqueValueInfos: uniqueValues.map((item) => ({
              value: item.value,
              symbol: {
                type: 'simple-marker',
                style: 'circle',
                size: 5,
                color: item.color,
                outline: {
                  type: 'simple-line',
                  style: config.dashPatterns[item.linetype?.toUpperCase()] || 'solid',
                  color: item.color,
                  width: item.linewidth === 0 ? 0.5 : [-1, -2, -3].includes(item.linewidth) ? 1 : item.linewidth > 0 ? item.linewidth / 5 : 0.1,
                },
              },
              label: item.label,
            })),
          };
        }

        // Apply Line Renderer (sublayer 2)
        if (layerLine) {
          layerLine.renderer = {
            type: 'unique-value',
            field: 'layer_id',
            uniqueValueInfos: uniqueValues.map((item) => ({
              value: item.value,
              symbol: {
                type: 'simple-line',
                style: config.dashPatterns[item.linetype?.toUpperCase()] || 'solid',
                color: item.color,
                width: item.linewidth === 0 ? 0.5 : [-1, -2, -3].includes(item.linewidth) ? 1 : item.linewidth > 0 ? item.linewidth / 5 : 0.1,
              },
              label: item.label,
            })),
          };
        }

        // Apply Polygon Renderer (sublayer 3)
        if (layerPolygon) {
          layerPolygon.renderer = {
            type: 'unique-value',
            field: 'layer_id',
            uniqueValueInfos: uniqueValues.map((item) => ({
              value: item.value,
              symbol: {
                type: 'simple-fill',
                color: [0, 0, 0, 0],
                style: 'none',
                outline: {
                  type: 'simple-line',
                  style: config.dashPatterns[item.linetype?.toUpperCase()] || 'solid',
                  color: item.color,
                  width: item.linewidth === 0 ? 0.5 : [-1, -2, -3].includes(item.linewidth) ? 1 : item.linewidth > 0 ? item.linewidth / 5 : 0.1,
                },
              },
              label: item.label,
            })),
          };
        }
        // add mapimage layer
        lasMap.add(mapimagelayer);
      };

      const applyLayerViewFilter = (siteId: string) => {
        const featureFilter = new FeatureFilter({
          where: `site_id = '${siteId}'`,
        });

        [polylineLayer, polygonLayer, point3DLayer, text3Dlayer].forEach((layer) => {
          layer.featureEffect = null; // clear any existing featureEffect
          lasView.whenLayerView(layer).then((layerView: any) => {
            layerView.filter = featureFilter;
          }).catch((err) => {
            console.error('whenLayerView failed:', err);
          });
        });
      };

      // Zoom to site function
      const zoomToSite = async (siteId: string): Promise<void> => {
        if (!siteId || siteId.trim() === '') {
          console.warn('Invalid site_id provided');
          return;
        }

        const defExpression = `site_id = '${siteId}'`;

        // Apply definition expressions
        fl.definitionExpression = defExpression;
        flText.definitionExpression = defExpression;
        flPoint.definitionExpression = defExpression;
        flLine.definitionExpression = defExpression;

        const parentMapImageLayer = (window as any).mapimagelayer;
        if (parentMapImageLayer) {
          mapImageLayerIndex.forEach((id) => {
            const sublayer = parentMapImageLayer.findSublayerById(id);
            if (sublayer) {
              sublayer.definitionExpression = defExpression;
            }
          });
        }

        applyLayerViewFilter(siteId);

        // In zoomToSite and setLayerDefinitions:
        const elevationInfo = getElevationInfo(siteId);
        text3Dlayer.elevationInfo = elevationInfo;
        point3DLayer.elevationInfo = elevationInfo;
        polygonLayer.elevationInfo = elevationInfo;
        polylineLayer.elevationInfo = elevationInfo;

        const has3D = await checkfor3dfeature(siteId);
        queryLayersTable(siteId, has3D);

        // Query all layers in parallel and union their extents
        try {
          const extentResults = await Promise.allSettled([
            fl.queryExtent(),
            flText.queryExtent(),
            flPoint.queryExtent(),
            flLine.queryExtent(),
          ]);

          // Collect all valid extents
          const validExtents = extentResults
            .filter(
              (result): result is PromiseFulfilledResult<__esri.FeatureLayerQueryExtentResult> =>
                result.status === 'fulfilled' && result.value.extent != null,
            )
            .map((result) => result.value.extent);

          if (validExtents.length > 0) {
            // Union all extents into one
            const combinedExtent = validExtents.reduce((acc, extent) => acc.union(extent));
            lasView.goTo(combinedExtent.expand(2.5), { duration: 1500 });
          }
        } catch (error) {
          console.error('Zoom query failed:', error);
        }
      };

      (window as any).zoomToSite = zoomToSite;

      const setDefs = (siteId: string) => {
        const defExpression = `site_id = '${siteId}'`;
        fl.definitionExpression = defExpression;
        const parentMapImageLayer = (window as any).mapimagelayer;
        if (parentMapImageLayer) {
          mapImageLayerIndex.forEach((id) => {
            const sublayer = parentMapImageLayer.findSublayerById(id);
            if (sublayer) {
              sublayer.definitionExpression = defExpression;
            }
          });
        }
        applyLayerViewFilter(siteId);

        const elev = getElevationInfo(siteId);
        text3Dlayer.elevationInfo = elev;
        point3DLayer.elevationInfo = elev;
        polygonLayer.elevationInfo = elev;
        polylineLayer.elevationInfo = elev;
      };

      const loadAndZoomSelectedOrder = async () => {
        // Use the currently selected order or find the first order with data
        const currentOrderId = selectedOrderId || placedOrders.find((order) => {
          const hasCadFiles = order.cadFiles && order.cadFiles.length > 0;
          const hasClassifiedPC = projectData?.lasFiles?.some(
            (las: any) => las.lasType !== 'unclassified' && las.ownerOrder === order._id,
          );
          return hasCadFiles || hasClassifiedPC;
        })?._id;

        if (!currentOrderId) return;

        const targetOrder = sortedPlacedOrders.find((o) => o._id === currentOrderId);
        if (!targetOrder) return;

        const cadIdsToProcess = (targetOrder.cadFiles ?? []).map((c: any) => c._id);

        // Apply setup for this order
        cadIdsToProcess.forEach(setDefs);
        if (initialOrder && initialOrder.orderStatus === orderStatusCode.done) {
          cadIdsToProcess.forEach((id) => queryLayersTable(id, getHas3dForSiteId(id)));
        }

        let combined: __esri.Extent | null = null;

        // CAD extents
        for (const cadId of cadIdsToProcess) {
          try {
            const { extent } = await fl.queryExtent({ where: `site_id='${cadId}'` });
            if (extent) combined = combined ? combined.union(extent) : extent;
          } catch (err) {
            console.warn(`Could not get extent for CAD ${cadId}`, err);
          }
        }

        // KML extent
        if (geoLayer?.fullExtent) {
          combined = combined ? combined.union(geoLayer.fullExtent) : geoLayer.fullExtent;
        }

        // Point-cloud extents: include unclassified + classified for selected order
        const currentPointClouds = pointCloudLayersRef.current || [];
        const relevantPointClouds = currentPointClouds.filter(
          (l: any) => {
            const lasFile = projectData?.lasFiles?.find(
              (f: any) => {
                const fullPath = f.filename || '';
                const title = fullPath.substring(fullPath.lastIndexOf('/') + 1) ||
                  f.lasFilename ||
                  `PointCloud-${projectData?.lasFiles.indexOf(f)}`;
                return title.replace(/\.(las|laz)$/, '') === l.title;
              },
            );
            // Include if unclassified OR belongs to current order
            return lasFile?.lasType === 'unclassified' || l.ownerOrder === currentOrderId;
          },
        );

        await Promise.all(relevantPointClouds.map((l) => l.load()));
        for (const l of relevantPointClouds) {
          if (l.fullExtent) combined = combined ? combined.union(l.fullExtent) : l.fullExtent;
        }

        // Geodatabase extents
        for (const l of geodatabaseLayersRef.current || []) {
          if (l.layers) {
            l.layers.forEach((sub: any) => {
              if (sub.fullExtent) combined = combined ? combined.union(sub.fullExtent) : sub.fullExtent.clone();
            });
          } else if (l.fullExtent) {
            combined = combined ? combined.union(l.fullExtent) : l.fullExtent.clone();
          }
        }

        // Shapefile extents
        for (const l of shapefileLayersRef.current || []) {
          if (l.layers) {
            l.layers.forEach((sub: any) => {
              if (sub.fullExtent) combined = combined ? combined.union(sub.fullExtent) : sub.fullExtent.clone();
            });
          } else if (l.fullExtent) {
            combined = combined ? combined.union(l.fullExtent) : l.fullExtent.clone();
          }
        }

        // Raster extents
        const rasterLayersInMap = lasView.map.layers.filter(
          (l) => l.type === 'base-tile' || l.type === 'raster' || l.type === 'imagery',
        );
        for (const l of rasterLayersInMap.toArray()) {
          try {
            await l.when();
            const extent = l.fullExtent || l.extent;
            if (extent) combined = combined ? combined.union(extent) : extent.clone();
          } catch (err) {
            console.warn('Could not get extent for raster layer', err);
          }
        }

        if (combined) {
          lasView.goTo(combined.expand(2.5), { duration: 1500 }).catch((err) => {
            if (err.name !== 'AbortError') console.error('Zoom failed:', err);
          });
        }
      };

      loadAndZoomSelectedOrder();

      const setLayerDefinitions = (siteId: string) => {
        if (!siteId || siteId.trim() === '') {
          console.warn('Invalid site_id provided');
          return;
        }
        const defExpression = `site_id = '${siteId}'`;
        fl.definitionExpression = defExpression;
        const parentMapImageLayer = (window as any).mapimagelayer;
        if (parentMapImageLayer) {
          mapImageLayerIndex.forEach((id) => {
            const sublayer = parentMapImageLayer.findSublayerById(id);
            if (sublayer) {
              sublayer.definitionExpression = defExpression;
            }
          });
        }
        applyLayerViewFilter(siteId);
        const elevationInfo = getElevationInfo(siteId);
        text3Dlayer.elevationInfo = elevationInfo;
        point3DLayer.elevationInfo = elevationInfo;
        polygonLayer.elevationInfo = elevationInfo;
        polylineLayer.elevationInfo = elevationInfo;
      };

      (window as any).setLayerDefinitions = setLayerDefinitions;

      // toggleTerrain globally
      (window as any).toggleTerrain = toggleTerrain;
      (window as any).geodatabaseLayers = geodatabaseLayersRef.current;
      (window as any).shapefileLayers = shapefileLayersRef.current;

      (window as any).getGeodatabaseLayerByFileId = (fileId: string) => geodatabaseLayersRef.current.find((l) => (l as any).fileId === fileId);

      (window as any).getShapefileLayerByFileId = (fileId: string) => shapefileLayersRef.current.find((l) => (l as any).fileId === fileId);

      reactiveUtils.whenOnce(() => lasView.ready).then(async () => {
        toggleTerrain([{ map: lasMapInstanceRef.current, name: 'LAS Map' }], true);
        let initialExt = lasView.extent.clone();
        initailExtentref.current = initialExt;
        const latVal = Math.round(initialExt.center.latitude);
        const longVal = Math.round(Math.abs(initialExt.center.longitude));
        // here 40 and 100 represents initial center for sceneview in redux
        // This condition is mainly handle when the user refreshes the browser the center is set to [40,-100] to check and assign the extent when the browser is refreshed
        if (latVal == parseInt('40') || longVal == parseInt('100')) {
          // call the extent function
          const layersExtent = await getFullLayersExtent(lasView);
          initialExt = layersExtent.clone();
          if (layersExtent) {
            lasView.goTo(layersExtent.expand(2.5), {
              duration: 1500,
            });
          }
        }
        const homelas = new Home({
          view: lasView,
        });
        const vp = new Viewpoint({
          targetGeometry: initialExt,
        });
        homelas.viewpoint = vp;
        setHomeWidget(homelas);

        try {
          // Get all point cloud layers in the map
          const pointCloudLayers = lasView.map.layers.filter(
            (layer) => layer.type === 'point-cloud',
          );

          if (pointCloudLayers.length === 0) {
            console.warn('No point cloud layers found in the map.');
          }
        } catch (err) {
          console.error('Error setting extent to point cloud layers:', err);
        }
      });

      const Area_Shape = (value) => Number((value * 10000000000).toFixed(4)); // approximating sq degree to sq metre
      const Length_Shape = (value) => Number((value * 100000).toFixed(4)); // approximating degree to metre

      // Monitor for WebGL context loss in the SceneView.
      // If a fatal WebGL error occurs (e.g., GPU reset, driver issue, tab suspended),
      // attempt to recover the view and restore the previous state.
      reactiveUtils.when(
        () => lasView.fatalError,
        () => {
          console.error(
            'Fatal Error! View has lost its WebGL context. Attempting to recover...',
          );

          // Preserve the current extent so the user’s view is not lost after recovery
          const currentExtent = lasView.extent;

          // Trigger ArcGIS WebGL recovery mechanism
          lasView.tryFatalErrorRecovery();

          // Reload the panorama scene to reinitialize WebGL-dependent resources
          loadPanoScene(
            currentFeatureRef.current,
            panoLayerFeatureRef.current,
            'first',
          );

          // Restore the extent once the view is recovered
          if (currentExtent) {
            lasView.extent = currentExtent;
          }
        },
      );
    };
    initializeLayers();

    return () => {
      active = false;
      if (lasViewToCleanup) {
        lasViewToCleanup.destroy();
      }
      setlasView(null);
      urlsToRevoke.forEach((url) => URL.revokeObjectURL(url));
    };
  }, [projectId, tokenRegistered, sortedPlacedOrders]);

  useEffect(() => {
    if (!tokenRegistered) return;
    if (sortedPlacedOrders.length > 0) {
      const isValidSelection = sortedPlacedOrders.some(
        (o) => o._id === selectedOrderId,
      );
      if (!isValidSelection) {
        dispatch(SetOrderSelectedAction(sortedPlacedOrders[0]._id));
      }
    }
  }, [sortedPlacedOrders, tokenRegistered]);

  // useEffect to handle loading Geodatabase and Shapefile layers based on selected order
  useEffect(() => {
    if (!tokenRegistered || !lasMapInstanceRef.current || !selectedOrderId) {
      return;
    }

    let active = true;

    const loadDataLayers = async () => {
      // 1. Cleanup: remove existing GDB/SHP layers from map
      if (geodatabaseLayersRef.current && geodatabaseLayersRef.current.length > 0) {
        lasMapInstanceRef.current.removeMany(geodatabaseLayersRef.current);
      }
      if (shapefileLayersRef.current && shapefileLayersRef.current.length > 0) {
        lasMapInstanceRef.current.removeMany(shapefileLayersRef.current);
      }

      const createGeodatabaseLayers = async (): Promise<any[]> => {
        try {
          if (!selectedOrder?.geoDbFiles || !Array.isArray(selectedOrder.geoDbFiles)) {
            console.warn('No geoDbFiles found in selected order.');
            return [];
          }

          // Sort by createdAt to find most recent with valid esriUrl
          const sortedValidFiles = selectedOrder.geoDbFiles
            .filter((file: any) => file.esriUrl && file.esriError?.toLowerCase() === 'success')
            .sort((a, b) => {
              const dateA = new Date(a.createdAt || 0).getTime();
              const dateB = new Date(b.createdAt || 0).getTime();
              return dateB - dateA; // Most recent first
            });

          if (!sortedValidFiles.length) {
            console.warn('No valid Geodatabase files found.');
            return [];
          }

          // Only load the most recent file
          const mostRecentFile = sortedValidFiles[0];
          const { esriUrl, filename, _id, createdAt } = mostRecentFile;
          const fileName = filename?.substring(filename.lastIndexOf('/') + 1) || 'Geodatabase-0';

          if (Array.isArray(esriUrl)) {
            const sublayers: GeoJSONLayer[] = [];

            for (let i = 0; i < esriUrl.length; i++) {
              const [layerName, geojsonUrl] = Object.entries(esriUrl[i])[0];

              try {
                let renderer: any;

                // Lookup layer-specific styles directly passed from configuration
                const layerConfig = styleConfig.layers[layerName];

                // Holds color specifically intended for mapping React UI sidebar list items
                let layerColorString: string | undefined;

                // Apply mapped config instantly if defined in imported styleConfig
                if (layerConfig) {
                  renderer = getRendererForConfig(layerConfig, layerConfig.geometryType, (c) => { layerColorString = c; });
                }
                const layerProps: any = {
                  url: GetEsriGeoJsonUrl(geojsonUrl),
                  title: `${layerName}`,
                  outFields: ['*'],
                  visible: true,
                };

                // Add active configuration renderer to GeoJSONLayer class parameters
                if (renderer) {
                  layerProps.renderer = renderer;
                }

                const layer = new GeoJSONLayer(layerProps);

                // If specific layer naming styleConfig is completely absent, dynamically assign
                // default style configurations based on the imported feature's recognized geometry type.
                if (!layerConfig) {
                  layer.when(() => {
                    let defConfig;
                    if (layer.geometryType === 'point' || layer.geometryType === 'multipoint') defConfig = styleConfig.defaults.point;
                    else if (layer.geometryType === 'polyline') defConfig = styleConfig.defaults.polyline;
                    else if (layer.geometryType === 'polygon') defConfig = styleConfig.defaults.polygon;

                    if (defConfig) {
                      const defRenderer = getRendererForConfig(defConfig, layer.geometryType, (c) => { layerColorString = c; });
                      if (defRenderer) {
                        layer.renderer = defRenderer;
                      }
                      (layer as any).layerColor = defConfig.symbol?.color;
                    }
                  }).catch(console.error);
                }

                (layer as any).fileId = _id;
                (layer as any).ownerOrder = selectedOrderId;
                (layer as any).layerId = i;
                (layer as any).layerName = layerName;
                if (layerColorString) {
                  (layer as any).layerColor = layerColorString;
                }

                sublayers.push(layer);
              } catch (layerErr) {
                console.error(`Error creating Geodatabase layer "${layerName}":`, layerErr);
              }
            }

            const groupLayer = new GroupLayer({
              id: _id,
              title: fileName,
              layers: sublayers,
              visible: true,
            });

            (groupLayer as any).ownerOrder = selectedOrderId;
            (groupLayer as any).fileId = _id;

            return [groupLayer];
          }
          return [];
        } catch (err) {
          console.error('Error creating Geodatabase layers:', err);
          return [];
        }
      };

      const createShapefileLayers = async (): Promise<any[]> => {
        try {
          if (!selectedOrder?.shpFiles || !Array.isArray(selectedOrder.shpFiles)) {
            console.warn('No shpFiles found in selected order.');
            return [];
          }

          // Sort by createdAt to find most recent with valid esriUrl
          const sortedValidFiles = selectedOrder.shpFiles
            .filter((file: any) => file.esriUrl && file.esriError?.toLowerCase() === 'success')
            .sort((a, b) => {
              const dateA = new Date(a.createdAt || 0).getTime();
              const dateB = new Date(b.createdAt || 0).getTime();
              return dateB - dateA; // Most recent first
            });

          if (!sortedValidFiles.length) {
            console.warn('No valid Shapefile files with esriUrl found.');
            return [];
          }

          // ONLY load the most recent file
          const mostRecentFile = sortedValidFiles[0];
          const { esriUrl, filename, _id, createdAt } = mostRecentFile;
          const fileName = filename?.substring(filename.lastIndexOf('/') + 1) || 'Shapefile-0';

          if (Array.isArray(esriUrl)) {
            const sublayers: GeoJSONLayer[] = [];

            for (let i = 0; i < esriUrl.length; i++) {
              const [layerName, geojsonUrl] = Object.entries(esriUrl[i])[0];

              try {
                let renderer: any;

                // Lookup layer-specific styles directly passed from configuration
                const layerConfig = styleConfig.layers[layerName];

                // Holds color specifically intended for mapping React UI sidebar list items
                let layerColorString: string | undefined;

                // Apply mapped config instantly if defined in imported styleConfig
                if (layerConfig) {
                  renderer = getRendererForConfig(layerConfig, layerConfig.geometryType, (c) => { layerColorString = c; });
                }
                const layerProps: any = {
                  url: GetEsriGeoJsonUrl(geojsonUrl),
                  title: `${layerName}`,
                  outFields: ['*'],
                  visible: true,
                };

                // Add active configuration renderer to GeoJSONLayer class parameters
                if (renderer) {
                  layerProps.renderer = renderer;
                }

                const layer = new GeoJSONLayer(layerProps);

                // If specific layer naming styleConfig is completely absent, dynamically assign
                // default style configurations based on the imported feature's recognized geometry type.
                if (!layerConfig) {
                  layer.when(() => {
                    let defConfig;
                    if (layer.geometryType === 'point' || layer.geometryType === 'multipoint') defConfig = styleConfig.defaults.point;
                    else if (layer.geometryType === 'polyline') defConfig = styleConfig.defaults.polyline;
                    else if (layer.geometryType === 'polygon') defConfig = styleConfig.defaults.polygon;

                    if (defConfig) {
                      const defRenderer = getRendererForConfig(defConfig, layer.geometryType, (c) => { layerColorString = c; });
                      if (defRenderer) {
                        layer.renderer = defRenderer;
                      }
                      (layer as any).layerColor = defConfig.symbol?.color;
                    }
                  }).catch(console.error);
                }

                (layer as any).fileId = _id;
                (layer as any).ownerOrder = selectedOrderId;
                (layer as any).layerId = i;
                (layer as any).layerName = layerName;
                if (layerColorString) {
                  (layer as any).layerColor = layerColorString;
                }

                sublayers.push(layer);
              } catch (layerErr) {
                console.error(`Error creating Geodatabase layer "${layerName}":`, layerErr);
              }
            }

              const groupLayer = new GroupLayer({
                id: _id,
                title: fileName,
                layers: sublayers,
                visible: true,
              });

              (groupLayer as any).ownerOrder = selectedOrderId;
              (groupLayer as any).fileId = _id;

              return [groupLayer];
            }
            return[];
        } catch (err) {
          console.error('Error creating Shapefile layers:', err);
          return [];
        }
      };

      const [geoDbLayers, shpLayers] = await Promise.all([
        createGeodatabaseLayers(),
        createShapefileLayers(),
      ]);

      if (!active) return;

      geodatabaseLayersRef.current = geoDbLayers;
      shapefileLayersRef.current = shpLayers;

      // Update window visibility getters
      (window as any).geodatabaseLayers = geoDbLayers;
      (window as any).shapefileLayers = shpLayers;

      if (lasMapInstanceRef.current) {
        lasViewref.goTo(geoDbLayers);
        lasMapInstanceRef.current.addMany([...geoDbLayers, ...shpLayers]);
      }
    };

    loadDataLayers();

    return () => {
      active = false;
    };
  }, [selectedOrderId, tokenRegistered, !!lasViewref]);

  // Adding Pano layer on map
  useEffect(() => {
    const map = lasViewref?.map;
    if (!tokenRegistered || !map || !panoFeatures || panoFeatures.length === 0) {
      if (panoLayerFeatureRef.current && map) {
        map.remove(panoLayerFeatureRef.current);
        panoLayerFeatureRef.current = null;
        panoLayerRef.current = null;
        setPanoControlsEnabled(false);
      }
      lastPanoStringRef.current = '';
      return;
    }

    const panoString = JSON.stringify(panoFeatures);
    if (panoString === lastPanoStringRef.current && map === lastMapUsedRef.current && panoLayerFeatureRef.current) {
      return; // avoid unnecessary re-rendering
    }

    // Remove old layer
    if (panoLayerFeatureRef.current && map) {
      map.remove(panoLayerFeatureRef.current);
    }

    const panofeatureCollection = {
      type: 'FeatureCollection',
      features: panoFeatures,
    };
    const panoBlob = new Blob([JSON.stringify(panofeatureCollection)], {
      type: 'application/json',
    });
    const panoUrl = URL.createObjectURL(panoBlob);
    const panoLayer = new GeoJSONLayer({
      url: panoUrl,
      title: 'Pano Features',
      outFields: ['*'],
      renderer: {
        type: 'simple',
        symbol: {
          type: 'simple-marker',
          style: 'circle',
          size: 8,
          color: [1, 161, 221, 1],
          outline: {
            color: [0, 55, 76, 1],
            width: 1,
          },
        },
      },
      visible: showPanoLayer,
    });
    panoLayer.elevationInfo = {
      mode: 'on-the-ground',
    };
    map.add(panoLayer);
    panoLayerFeatureRef.current = panoLayer as any;
    panoLayerRef.current = panoLayer;
    panoLayer.when(() => {
      panoLayer.visible = panoVisible;
    });
    setPanoControlsEnabled(true);
    lastPanoStringRef.current = panoString;
    lastMapUsedRef.current = map;

    return () => {
      if (panoLayerFeatureRef.current && map) {
        map.remove(panoLayerFeatureRef.current);
      }
      URL.revokeObjectURL(panoUrl);
      panoLayerFeatureRef.current = null;
      panoLayerRef.current = null;
    };
  }, [tokenRegistered, panoFeatures, showPanoLayer, lasViewref]);

  useEffect(() => {
    if (!selectedOrderId || !lasViewref) return;

    // Remove existing widget if any
    if (annotationWidgetRef3d.current) {
      lasViewref.ui.remove(annotationWidgetRef3d.current);
      annotationWidgetRef3d.current.destroy();
      annotationWidgetRef3d.current = null;
    }

    const AnnotationEdit3d = new Annotation({
      view: lasViewref,
      clicked: true,
      otherwidgetClicked: false,
    });
    annotationWidgetRef3d.current = AnnotationEdit3d;
    setAnnotationEdit(AnnotationEdit3d);
    lasViewref.ui.add(AnnotationEdit3d, 'top-right');
  }, [selectedOrderId, lasViewref]);

  // Memoize KML string to prevent unnecessary re-renders
  const kmlString = useMemo(() => {
    if (!featureCollection?.features?.length) return '';
    return JSON.stringify(featureCollection);
  }, [featureCollection]);

  // Adding KML layer on map
  useEffect(() => {
    const map = lasViewref?.map;

    // Handle empty/no data case
    if (!tokenRegistered || !map || !kmlString) {
      if (geoLayerRef.current && map) {
        map.remove(geoLayerRef.current);
        geoLayerRef.current = null;
        setGeoLayer(null);
      }
      lastKmlStringRef.current = '';
      return;
    }

    // Skip if data hasn't changed and layer exists
    if (kmlString === lastKmlStringRef.current && geoLayerRef.current) {
      return;
    }

    // Remove old layer before creating new one
    if (geoLayerRef.current && map) {
      map.remove(geoLayerRef.current);
      geoLayerRef.current = null;
    }

    const blob = new Blob([kmlString], { type: 'application/json' });
    const geoJsonUrl = URL.createObjectURL(blob);

    const newGeoLayer = new GeoJSONLayer({
      url: geoJsonUrl,
      title: 'KML Features',
      renderer: {
        type: 'simple',
        symbol: {
          type: 'simple-fill',
          color: [0, 0, 0, 0],
          outline: {
            color: '#5bafad',
            width: 2,
          },
        },
      },
      visible: true,
    });

    map.add(newGeoLayer);
    geoLayerRef.current = newGeoLayer;
    setGeoLayer(newGeoLayer);
    lastKmlStringRef.current = kmlString;

    // Only revoke URL on cleanup, don't remove layer ref
    return () => {
      URL.revokeObjectURL(geoJsonUrl);
    };
  }, [tokenRegistered, kmlString, lasViewref]);

  // Adding Raster layers on map
  useEffect(() => {
    const map = lasViewref?.map;
    if (!tokenRegistered || !map || !rasterTileJson) {
      if (rasterLayers && rasterLayers.length > 0 && map) {
        rasterLayers.forEach((r) => map.remove(r.layer));
        setRasterLayers([]);
        (window as any).rasterLayers = [];
      }
      lastRasterStringRef.current = '';
      return;
    }

    const rasterString = JSON.stringify(rasterTileJson);
    if (rasterString === lastRasterStringRef.current && map === lastMapUsedRef.current) {
      return; // avoiding unnecessary re-rendering
    }

    // Remove old layers
    if (rasterLayers && rasterLayers.length > 0) {
      rasterLayers.forEach((r) => map.remove(r.layer));
    }

    const newRasterList: any[] = []; // array to store new raster layers
    Object.entries(rasterTileJson).forEach(([layerName, layerData]: [string, any]) => {
      const urlTemplate = layerData?.tileUrl;
      const isTms = layerData?.isTms ?? false;

      if (urlTemplate) {
        const rasterTileLayer = new RasterTileLayer({
          urlTemplate,
          isTms,
          opacity: 1,
          title: layerName,
        });
        map.add(rasterTileLayer, 0); // Put at the bottom
        newRasterList.push({ name: layerName, layer: rasterTileLayer });
      }
    });

    setRasterLayers(newRasterList);
    (window as any).rasterLayers = newRasterList; // storing raster layers in window object for easy access
    lastRasterStringRef.current = rasterString;
    lastMapUsedRef.current = map;
  }, [tokenRegistered, rasterTileJson, lasViewref]);

  // Adding Point Cloud layers on map
  useEffect(() => {
    const map = lasViewref?.map;
    if (!tokenRegistered || !map || !projectData?.lasFiles) {
      if (pointCloudLayersRef.current && pointCloudLayersRef.current.length > 0 && map) {
        pointCloudLayersRef.current.forEach((l) => map.remove(l));
        setPointCloudLayers([]);
        pointCloudLayersRef.current = [];
      }
      lastPointCloudStringRef.current = '';
      return;
    }

    const lasString = JSON.stringify(projectData.lasFiles);
    if (lasString === lastPointCloudStringRef.current && map === lastPointCloudMapRef.current) {
      return; // avoid unnecessary re-rendering
    }

    // Remove old layers
    if (pointCloudLayersRef.current && pointCloudLayersRef.current.length > 0) {
      pointCloudLayersRef.current.forEach((l) => map.remove(l));
    }

    const newLayers = createPointCloudLayers();
    if (newLayers.length > 0) {
      map.addMany(newLayers);
      setPointCloudLayers(newLayers);
      pointCloudLayersRef.current = newLayers;

      // Sync checkedItems
      const initialCheckedItems = newLayers.reduce((acc, pcl) => {
        const title = pcl.title || 'PointCloud';
        acc[title] = lasFilesDisplayStatuses[title] ?? true;
        pcl.visible = acc[title];
        return acc;
      }, {} as { [key: string]: boolean });
      setCheckedItems((prev) => ({ ...prev, ...initialCheckedItems }));
    }

    lastPointCloudStringRef.current = lasString;
    lastPointCloudMapRef.current = map;
  }, [tokenRegistered, projectData?.lasFiles, lasViewref, createPointCloudLayers, lasFilesDisplayStatuses, getLasTitle]);

  // AOI Widget for KML
  useEffect(() => {
    if (!geoLayer || !lasViewref || !featureCollection?.features?.length) return;

    const AOIWidget = new AOI({
      view: lasViewref,
    });
    setAoiWidget(AOIWidget);
    lasViewref.ui.add(AOIWidget, 'top-right');
    const handle = AOIWidget.on('AOI-toggle', ({ active }) => {
      if (geoLayerRef.current) {
        geoLayerRef.current.visible = !active;
      }
    });

    return () => {
      if (lasViewref) {
        lasViewref.ui.remove(AOIWidget);
      }
      setAoiWidget(null);
      handle.remove();
    };
  }, [geoLayer, lasViewref, featureCollection, AnnotationEdit]);

  useEffect(() => {
    if (!homeWidget || !lasViewref) return;

    // To ensure Home is always at the bottom, we re-add it whenever
    // Annotation or AOI widgets might have been re-added.
    lasViewref.ui.remove(homeWidget);
    lasViewref.ui.add(homeWidget, 'top-right');
  }, [homeWidget, lasViewref, AnnotationEdit, aoiWidget]);

  useEffect(() => {
    if (panoLayerFeatureRef.current) {
      panoLayerFeatureRef.current.visible = panoVisible;
    }
  }, [panoVisible]);

  useEffect(() => {
    const panoLayer = panoLayerRef.current;
    if (!panoLayer) return;

    panoLayer.renderer = {
      type: 'unique-value',
      field: 'object_id',
      uniqueValueInfos: failedPanoIds.map((id) => ({
        value: id,
        symbol: {
          type: 'simple-marker',
          style: 'circle',
          size: 8,
          color: [160, 160, 160, 1], // GREY
          outline: {
            color: [120, 120, 120, 1],
            width: 1,
          },
        },
        label: 'Failed pano',
      })),
      defaultSymbol: {
        type: 'simple-marker',
        style: 'circle',
        size: 8,
        color: [1, 161, 221, 1], // NORMAL BLUE
        outline: {
          color: [0, 55, 76, 1],
          width: 1,
        },
      },
    };
  }, [failedPanoIds, panoFeatures]);

  useEffect(() => {
    if (!homeWidget || !lasViewRef.current || !selectedOrder?.middlePoint) return;

    const view = lasViewRef.current;

    const { longitude, latitude } = selectedOrder.middlePoint;

    const vp = new Viewpoint({
      camera: {
        position: {
          longitude,
          latitude,
          z: 1500,
        },
        heading: 0,
        tilt: 0,
      },
    });

    view.goTo(vp, {
      duration: 1500,
      easing: 'ease-in-out',
    });

    homeWidget.viewpoint = vp;
  }, [homeWidget, selectedOrder]);

  useEffect(() => {
    const annotationwidget3d = annotationWidgetRef3d.current;
    if (!annotationwidget3d) return;
    if (annotationwidget3d) {
      annotationwidget3d.on('open-annotations', async () => {
        annotationWidgetRef3d.current.otherWidget = false;
        otherwidgetactiveref.current = false;
        InactiveWidgetClose(annotationWidgetRef3d.current, true);
      });
      annotationwidget3d.on('annotate-toggle', async ({ active }) => {
        annotationWidgetRef3d.current.otherWidget = false;
        isAddingRef3d.current = active;
        ifStart.current = false;
        setIsAdding(active);
        setAnnotations3d([]);
        existingAnnotaion.current = [];
      });
      annotationwidget3d.on('annotate-start', async ({ active }) => {
        annotationWidgetRef3d.current.otherWidget = false;
        (window as any).newannotationclicked = true;
        if (lasViewref?.container) lasViewref.container.style.cursor = 'crosshair';
        isAddingRef3d.current = true;
        ifStart.current = true;
        newLabelRef.current = true;
        const lasrect = lasRef.current.getBoundingClientRect();
        setOverlaySize3d({
          width: lasrect.width,
          height: lasrect.height,
        });
        setOverlayPosition3d({
          top: lasrect.top,
          left: lasrect.left,
        });
        setIsAdding(active);
        setAnnotations3d([]);
        existingAnnotaion.current = [];
      });
      const fetchHandle = annotationwidget3d.on('fetch-annotations', async () => {
        const annotateOrder = orderref.current || orderid;
        const match = AnnotationFeatures[annotateOrder];
        if (match) {
          const result = match;
          isAddingRef3d.current = true;
          setIsAdding(true);
          const { features } = result;
          if (result?.features) {
            setAnnotations3d(features);
            existingAnnotaion.current = features;
            newLabelRef.current = false;
            const lasrect = lasRef.current.getBoundingClientRect();
            setOverlaySize3d({
              width: lasrect.width,
              height: lasrect.height,
            });
            setOverlayPosition3d({
              top: lasrect.top,
              left: lasrect.left,
            });
          }
        }
      });
      const hideAnnotation = annotationwidget3d.on('hide-annotations', () => {
        isAddingRef3d.current = false;
        setIsAdding(false);
        ifStart.current = false;
        newLabelRef.current = true;

        isAddingRef.current = true;
        const lasrect = lasRef.current.getBoundingClientRect();
        setOverlaySize3d({
          width: 0,
          height: 0,
        });
        setOverlayPosition3d({
          top: lasrect.top,
          left: lasrect.left,
        });
        setAnnotations3d([]);
        existingAnnotaion.current = [];
      });
    }
  }, [AnnotationFeatures, AnnotationEdit]);

  useEffect(() => {
    if (!distanceWidget3d) return;

    const handles: any[] = [];

    if (distanceWidget3d) {
      handles.push(
        distanceWidget3d.on('measurement-toggle', (e: { active: boolean }) => {
          measuringRefDistance.current = e.active;
          SetMeasurmentActiveDis(e.active);
          SetMeasurmentActiveArea(!e.active);
          InactiveWidgetClose(distanceWidget3d, true);
          if (e.active) setShowPopup(false);
        }),
      );
    }

    return () => handles.forEach((h) => h.remove());
  }, [distanceWidget3d]);

  useEffect(() => {
    if (!AreaWidget3d) return;
    const handles: any[] = [];

    if (AreaWidget3d) {
      handles.push(
        AreaWidget3d.on('measurement-toggle', (e: { active: boolean }) => {
          measuringRefArea.current = e.active;
          SetMeasurmentActiveArea(e.active);
          SetMeasurmentActiveDis(!e.active);
          InactiveWidgetClose(AreaWidget3d, true);
          if (e.active) setShowPopup(false);
        }),
      );
    }
    return () => handles.forEach((h) => h.remove());
  }, [AreaWidget3d]);

  useEffect(() => {
    if (!basemapRef.current) return;
    const handles: any[] = [];

    if (basemapRef.current) {
      handles.push(
        basemapRef.current.on('basemap-toggle', (e: { active: boolean }) => {
          InactiveWidgetClose(basemapRef.current, true);
          basemapRef.current.isActive = true;
           if (lasViewref?.container) lasViewref.container.style.cursor = 'default';
          (window as any).basemapOpened = true;
        }),
        basemapRef.current.on('basemap-toggle-close', (e: { active: boolean }) => {
          basemapRef.current.isActive = false;
          (window as any).basemapOpened = false;
        }),
      );
    }
    return () => handles.forEach((h) => h.remove());
  }, [basemapRef.current]);

  // handle panoLayer visibility
  useEffect(() => {
    const panoLayer = panoLayerRef.current;
    const coneGraphic = coneGraphicRef.current;

    if (panoLayer) panoLayer.visible = panoVisible;
    if (coneGraphic) coneGraphic.visible = panoVisible;
  }, [panoVisible]);

  // Cleanup sliders
  useEffect(() => () => {
    [inputSliderRef, outputSliderRef, classSliderRef].forEach((ref) => {
      ref.current?.destroy();
    });
  }, []);

  useEffect(() => {
    if (!selectedOrderId || !lasViewRef.current) return;
    if (!mapImageLayer || !featureLayers.length) return;

    const selectedOrder = sortedPlacedOrders.find((o) => o._id === selectedOrderId);
    if (!selectedOrder) return;
    const cadIdsForOrder = (selectedOrder.cadFiles ?? []).map((c: any) => c._id);
    if (!cadIdsForOrder.length) return;

    // Update definition expressions for all layers with proper null checks
    const updateLayerDefs = (siteIds: string[]) => {
      const whereClause = `site_id = '${siteIds}'`;

      // Update MapImageLayers with null checks
      if (mapImageLayer) {
        mapImageLayerIndex.forEach((subId) => {
          const sublayer = mapImageLayer.findSublayerById(subId);
          if (sublayer && sublayer.definitionExpression !== undefined) {
            try {
              sublayer.definitionExpression = whereClause;
            } catch (err) {
              console.warn(`Failed to set definition expression on sublayer ${subId}:`, err);
            }
          }
        });
      }

      // Update FeatureLayers with null checks
      if (featureLayers && featureLayers.length > 0) {
        featureLayers.forEach((layer) => {
          if (layer && layer.definitionExpression !== undefined) {
            try {
              layer.definitionExpression = whereClause;
            } catch (err) {
              console.warn('Failed to set definition expression on feature layer:', err);
            }
          }
        });
      }
    };

    // updateLayerDefs(cadIdsForOrder);
    const activeSiteId = cadIdsForOrder[cadIdsForOrder.length - 1];
    if (activeSiteId) {
      updateLayerDefs(activeSiteId); //single siteId only
    }

    // Update point cloud layers visibility
    if (pointCloudLayers && pointCloudLayers.length > 0) {
      const isOrderSwitch = lastOrderIdRef.current !== selectedOrderId;

      pointCloudLayers.forEach((layer) => {
        if (!layer) return;

        const lasFile = projectData?.lasFiles?.find(
          (f: any) => getLasTitle(f) === layer.title,
        );

        // For classified point clouds, manage auto-visibility on order switch
        if (lasFile && lasFile.lasType !== 'unclassified') {
          const belongsToCurrentOrder = lasFile.ownerOrder === selectedOrderId;

          if (isOrderSwitch) {
            layer.visible = belongsToCurrentOrder;
            setLasFilesDisplayStatuses((prev) => ({
              ...prev,
              [layer.title]: belongsToCurrentOrder,
            }));
          } else {
            // Not switching orders: respect current sidebar state
            layer.visible = lasFilesDisplayStatuses[layer.title] ?? belongsToCurrentOrder;
          }
        } else if (lasFile && lasFile.lasType === 'unclassified') {
          // Unclassified follow sidebar state
          layer.visible = lasFilesDisplayStatuses[layer.title] ?? true;
        }
      });
    }

    // Query and apply rendering for each CAD
    if (selectedOrder && selectedOrder.orderStatus === orderStatusCode.done) {
      cadIdsForOrder.forEach((cadId) => {
        if ((window as any).queryLayersTable) {
          try {
            (window as any).queryLayersTable(cadId);
          } catch (err) {
            console.warn(`Failed to query layers table for CAD ${cadId}:`, err);
          }
        }
      });
    }

    if (geodatabaseLayersRef.current && geodatabaseLayersRef.current.length > 0) {
      geodatabaseLayersRef.current.forEach((layer) => {
        const shouldBeVisible = (layer as any).ownerOrder === selectedOrderId;
        layer.visible = shouldBeVisible;
      });
    }

    // Update Shapefile layers visibility
    if (shapefileLayersRef.current && shapefileLayersRef.current.length > 0) {
      shapefileLayersRef.current.forEach((layer) => {
        const shouldBeVisible = (layer as any).ownerOrder === selectedOrderId;
        layer.visible = shouldBeVisible;
      });
    }
    lastOrderIdRef.current = selectedOrderId;
  }, [selectedOrderId, placedOrders, featureLayers, mapImageLayer, projectData?.lasFiles, getLasTitle]);

  /// Widget Toggle Function
  const InactiveWidgetClose = (widget: any, state: boolean) => {
    // get widget
    const widgetState = state;

    const widgetManager = {
      distance: distanceWidget3d,
      area: AreaWidget3d,
      basemap: basemapRef.current,
      annotation: annotationWidgetRef3d.current,
    };

    Object.values(widgetManager).forEach((Widget) => {
      if (!Widget || Widget.name === widget.name) return;

      Widget.isActive = !widgetState;

      if (Widget.name === 'distanceWidget3d') {
        distanceWidget3d?.measurement?.viewModel?.clear();
      } else if (Widget.name === 'AreaWidget3d') {
        AreaWidget3d?.measurement?.viewModel?.clear();
      } else if (Widget.name === 'basemapWidget') {
        Widget.isActive = false;
        basemapRef.current.isActive = false;
        (window as any).basemapOpened = false;
      } else if (Widget.name === 'AnnotationEdit') {
        Widget.isActive = false;
        widget.showExisting = false;
        Widget.showPanel = false;
        Widget.otherWidget = true;
        Widget._closePanel();
        newLabelRef.current = false;
        otherwidgetactiveref.current = true;
      }
    });
  };

  const handleViewVisibility = (view: string) => {
    const views = { las: lasVisible, pano: panoVisible };
    const visibleCount = Object.values(views).filter(Boolean).length;
    if (visibleCount === 1 && views[view]) return;
    // Toggle the selected view
    if (view === 'pano') {
      setPanoVisible((prev) => !prev);
    }
  };
  const containerWidth = useMemo(() => {
    const count = [lasVisible, panoVisible].filter(Boolean).length;

    if (count === 1) return 1000;
    if (count === 2) return 500;

    return 1000; // fallback
  }, [panoVisible]);
  async function getFullLayersExtent(view) {
    await reactiveUtils.whenOnce(() => view.ready);
    let pointCloudExtent: Extent | null = null;
    let tileExtent: Extent | null = null;

    const layers = view.map.layers.toArray();

    for (const layer of layers) {
      try {
        await layer.when();
        const extent = layer.fullExtent || layer.extent;
        if (!extent) continue;

        // Skip global generic extents (oversized services)
        if (extent.width > 1000000 || extent.height > 1000000) {
          continue;
        }
        if (
          layer.type === 'point-cloud' ||
          layer.type === 'scene'
        ) {
          pointCloudExtent = pointCloudExtent
            ? pointCloudExtent.union(extent)
            : extent.clone();
          continue;
        }

        if (
          layer.type === 'base-tile' ||
          layer.type === 'raster' ||
          layer.type === 'imagery'
        ) {
          tileExtent = tileExtent
            ? tileExtent.union(extent)
            : extent.clone();
          continue;
        }

        // FeatureLayers (CAD, KML, Geodb, Shp)
        if (layer.type === 'feature' || layer.type === 'geojson') {
          tileExtent = tileExtent
            ? tileExtent.union(extent)
            : extent.clone();
        }
      } catch {

      }
    }

    if (pointCloudExtent && tileExtent) {
      return pointCloudExtent.union(tileExtent);
    }
    return pointCloudExtent || tileExtent || null;
  }
  // call labeleditor function
  return (
    <>
      {loading && <LoadingSpinner loading={loading} />}
      {!loading && (
        <>
          <FilesSidebar
            key={selectedOrderId}
            lasFilesWith3DTile={projectData?.lasFiles || []}
            showPointCloud={showPointCloud}
            setShowPointCloud={setShowPointCloud}
            showClassifiedPointCloud={showClassifiedPointCloud}
            setShowClassifiedPointCloud={setShowClassifiedPointCloud}
            setResetZoom={setResetZoom}
            toggleLasVisibility={toggleLasVisibility}
            lasFilesDisplayStatuses={lasFilesDisplayStatuses}
            onToggleSidebar={setIsSidebarVisible}
            isSidebarVisible={isSidebarVisible}
            showPanoLayer={showPanoLayer}
            setShowPanoLayer={setShowPanoLayer}
            toggleTerrain={toggleTerrain}
            classVisibility={classVisibility}
            toggleUnknownClassVisibility={toggleUnknownClassVisibility}
            toggleClassVisibility={toggleClassVisibility}
            openAdjustPanel={openAdjustPanel}
            onAdjustPanelPositionChange={setAdjustPanelPosition}
            view={lasViewref}
            geodatabaseLayersRef={geodatabaseLayersRef}
            shapefileLayersRef={shapefileLayersRef}
          />
          {/* CLASSIFIED POINT CLOUD PANEL */}
          {classifiedPanelOpen && (
            <ClickAwayListener
              key="classified-panel"
              onClickAway={() => {
                setClassifiedPanelOpen(false);
                setAdjustPanelPosition(null);
              }}
            >
              <Box
                sx={{
                  ...styles.clickaway, ...getAdjustPanelStyles(styles.classifiedclickaway),
                }}
              >
                <Box sx={styles.pointsizeslidebox}>
                  <Box sx={styles.pointsizeslidetypo}>
                    <Typography variant="body2" component="span">
                      Classified Point Cloud
                    </Typography>
                    <Typography variant="body2" component="span" sx={styles.maxValueLabel}>
                      {classifiedPointSize % 1 === 0 ? classifiedPointSize.toFixed(0) : classifiedPointSize.toFixed(1)}
                    </Typography>
                  </Box>
                  <Box id="classified-point-size-slider" key="classified-slider" sx={styles.pointercolor} />
                </Box>
              </Box>
            </ClickAwayListener>
          )}

          {/* OUTPUT 3D POINTS PANEL (AERIAL) */}
          {outputPanelOpen && (
            <ClickAwayListener
              key="output-panel"
              onClickAway={() => {
                setOutputPanelOpen(false);
                setAdjustPanelPosition(null);
              }}
            >
              <Box
                sx={{
                  ...styles.clickaway, ...getAdjustPanelStyles(styles.output3d),
                }}
              >
                <Box sx={styles.pointsizeslidebox}>
                  <Box sx={styles.pointsizeslidetypo}>
                    <Typography variant="body2" component="span">
                      Output 3D Points
                    </Typography>
                    <Typography variant="body2" component="span" sx={styles.maxValueLabel}>
                      {outputPointSize % 1 === 0 ? outputPointSize.toFixed(0) : outputPointSize.toFixed(1)}
                    </Typography>
                  </Box>
                  <Box id="output-point-size-slider" key="output-slider" sx={styles.pointercolor} />
                </Box>
              </Box>
            </ClickAwayListener>
          )}

          {/* INPUT POINT CLOUD PANEL */}
          {inputPanelOpen && (
            <ClickAwayListener
              key="input-panel"
              onClickAway={() => {
                setInputPanelOpen(false);
                setAdjustPanelPosition(null);
              }}
            >
              <Box
                sx={{
                  ...styles.clickaway, ...getAdjustPanelStyles(styles.unclassifiedclickaway),
                }}
              >
                <Box sx={styles.pointsizeslidebox}>
                  <Box sx={styles.pointsizeslidetypo}>
                    <Typography variant="body2" component="span">
                      Input Point Cloud
                    </Typography>
                    <Typography variant="body2" component="span" sx={styles.maxValueLabel}>
                      {inputPointSize % 1 === 0 ? inputPointSize.toFixed(0) : inputPointSize.toFixed(1)}
                    </Typography>
                  </Box>
                  <Box id="input-point-size-slider" key="input-slider" sx={styles.pointercolor} />
                </Box>
              </Box>
            </ClickAwayListener>
          )}
          {/* Document Icon on Map View - Initially hidden, shown when sidebar is hidden */}
          <IconButton
            onClick={() => setIsSidebarVisible(!isSidebarVisible)}
            sx={styles.mapViewToggleButton(isSidebarVisible)}
            aria-label={isSidebarVisible ? 'Hide Sidebar' : 'Show Sidebar'}
          >
            <SvgIcon sx={styles.sidebarsvg}>
              <rect fill="transparent" width="24" height="24" />
              <polygon
                points="15.83,2.5 4.17,2.5 2.5,4.17 2.5,15.83 4.17,17.5 15.83,17.5 17.5,15.83 17.5,4.17"
                fill="none"
                stroke="#021E40"
                strokeWidth="1.46"
              />
              <polygon
                points="7.5,2.5 7.5,17.5"
                fill="none"
                stroke="#021E40"
                strokeWidth="1.46"
              />
              <polygon
                points="13.33,12.5 10.83,10 13.33,7.5"
                fill="none"
                stroke="#021E40"
                strokeWidth="1.46"
              />
            </SvgIcon>
          </IconButton>
        </>
      )}
      <div id="esridiv" style={{ ...styles.esriView, display: loading ? 'none' : 'flex' }}>
        <div style={styles.lasView}>
          <div id="lasDiv" ref={lasRef} style={{ width: '100%', height: '100%' }} />
          {lasViewref && (
            <div style={styles.OverlaylasView(overlaySize3d, overlayposition3d)}>
              <LabelEditor
                view={lasViewref}
                active={isAddingRef3d.current}
                annotations={ifStart.current ? annotations3d : annotations3d}
                orderId={orderref.current}
                newLabel={newLabelRef.current}
                stopnewLabel={false}
                viewWidthNew={containerWidth}
                otherwidgetactive={otherwidgetactiveref.current}
                basemapactive={basemapRef.current.isActive}
              />
            </div>
          )}
        </div>
        <div style={styles.panoView}>
          <div id="panoDiv" ref={panoRef} style={{ width: '100%', height: '100%', position: 'relative' }}>
            {/* Injecting CSS to hide Pannellum library controls */}
            <style>{styles.panoControlsCss}</style>
            {panoVisible && imageSource ? (
              <ReactPannellum
                key={imageSource}
                id="1"
                sceneId="firstScene"
                imageSource={imageSource}
                config={config.panoConfig}
                style={styles.panoViewDiv}
                onPanoramaMouseDown={handlePanoMouseDown}
                onPanoramaMouseUp={handlePanoMouseUp}
                onLoad={async () => {
                  ReactPannellum.resize();
                }}
              />
            ) : (
              <div style={styles.placeholderWrapper}>
                <div style={styles.placeholderCard}>
                  <div style={styles.placeholderIcon}>{'\u2139'}</div>
                  <div>
                    <div style={styles.placeholderTitle}>Choose a start point</div>
                    <div style={styles.placeholderSubtitle}>
                      Select a point on the map to begin.
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
          {/* Pano Popup Overlay */}
          {(panoError || panoWarning) && !panoErrorClosed && (
            <div style={styles.panoErrorOverlay}>
              <div style={styles.panoErrorPopup}>
                <div style={panoWarning ? styles.WarningPlaceholderIcon : styles.ErrorplaceholderIcon}>
                  {panoWarning ? 'i' : '!'}
                </div>
                <div>
                  <div style={styles.placeholderTitle}>{panoError || panoWarning}</div>
                  <div style={styles.placeholderSubtitle}>
                    Contact
                    {' '}
                    <a
                      href={config.airworks_SupportUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={styles.supportLink}
                    >
                      support
                    </a>
                    {' '}
                    if the error persists.
                  </div>
                </div>
                <button
                  style={styles.errorCloseBtn}
                  onClick={() => { setPanoError(null); setPanoWarning(null); setPanoErrorClosed(true); }}
                >
                  ✕
                </button>
              </div>
            </div>
          )}
        </div>

        {filteredFeatures.length > 0 && showPopup && islasViewRef.current && filteredFeatures[currentIndex] && (
          <PopupWindow
            x={popupXY.x}
            y={popupXY.y}
            attributes={filteredFeatures[currentIndex]}
            index={currentIndex}
            totalCount={filteredFeatures.length}
            onPrev={() => {
              const newIndex = currentIndex - 1;
              setCurrentIndex(newIndex);
              if ((window as any).__highlightFeature) {
                (window as any).__highlightFeature(newIndex);
              }
            }}
            onNext={() => {
              const newIndex = currentIndex + 1;
              setCurrentIndex(newIndex);
              if ((window as any).__highlightFeature) {
                (window as any).__highlightFeature(newIndex);
              }
            }}
            onClose={() => {
              setShowPopup(false);
              const hl = lasViewRef.current?.map?.findLayerById('highlight-layer') as any;
              if (hl) hl.removeAll();
              (window as any).__highlightFeature = null;
            }}
            parentRef={null}
          />
        )}

        <div
          id="selectView"
          style={styles.selectView}
          onMouseEnter={() => setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
        >
          {/* Tooltip */}
          {!panoControlsEnabled && isHovered && (
            <div style={styles.noPanoTooltip}>
              No pano available
            </div>
          )}

          <SVG
            src={panoVisible ? eyeHidden : eyeVisible}
            onClick={
              panoControlsEnabled ? () => handleViewVisibility('pano') : undefined
            }
            style={panoVisible ? styles.eyeHidden : styles.eyeVisible}
          />

          <button
            onClick={
              panoControlsEnabled ? () => handleViewVisibility('pano') : undefined
            }
            style={panoVisible ? styles.toggleButton : styles.toggleButtonOff}
          >
            {panoVisible ? 'HIDE PANO' : 'SHOW PANO'}
          </button>
        </div>

      </div>
    </>
  );
};

export default EsriSplitViewer;
